import React, { useState, useRef, useEffect, useContext } from 'react';
import styles from './reportCreator.module.css';
import { 
  FileText,
  File, 
  BarChart3, 
  BarChart,
  BarChart2,
  CheckSquare,
  PieChart, 
  Table, 
  Type, 
  Sparkles, 
  Download, 
  Plus, 
  GripVertical, 
  Trash2, 
  Settings,
  Save,
  TrendingUp,
  Calendar,
  Hash,
  Quote,
  Code,
  Send,
  Brain,
  ChevronDown,
  ChevronRight,
  Eye,
  Edit,
  ChevronLeft,
  ArrowLeft,
  Heading1,
  Undo,
  Redo,
  Video,
  // New advanced block icons
  Layout,
  Square,
  Maximize,
  Layers,
  Clock,
  Image,
  Globe,
  Move,
  // New AI and document icons
  Presentation,
  Target,
  TrendingDown,
  Search,
  MessageCircle,
  Wand2,
  Check,
  DollarSign,
  Users,
  Variable,
  X
} from 'lucide-react';
import { templates, pageTemplates, slashMenuItems, mockData, templateBlocks, aiEnhancementOptions, availableVariables } from './reportData';
import { AuthContext } from 'components/lib';
import axios from 'axios';
import CloseNav from '../nav';
import { ReportCarousel } from './reportCarousel';
import ReportBlock from '../../../../components/ReportBlock';
import TemplateSaveDialog from './templateSaveDialog';
import { ChartSettingsForm } from './chartSettingsForm';
import { validateAIContent } from '../../../../utils/aiContentValidator';

const iconMap = {
  FileText,
  File,
  BarChart3,
  BarChart,
  BarChart2,
  CheckSquare,
  PieChart,
  Table,
  Type,
  Sparkles,
  Download,
  Plus,
  GripVertical,
  Trash2,
  Settings,
  TrendingUp,
  Calendar,
  Hash,
  Quote,
  Code,
  Send,
  Brain,
  ChevronDown,
  ChevronRight,
  Eye,
  Edit,
  ChevronLeft,
  Heading1,
  Video,
  // New advanced block icons
  Layout,
  Square,
  Maximize,
  Layers,
  Clock,
  Image,
  Globe,
  Move,
  // AI and document icons
  Presentation,
  Target,
  TrendingDown,
  Check,
  DollarSign,
  Users,
  Search,
  MessageCircle,
  Variable,
  Save,
  X
};

export function ReportCreator() {
  const [analysisData, setAnalysisData] = useState(null);
  const [reportApiData, setReportApiData] = useState(null);
  const [documents, setDocuments] = useState([
    {
      id: 'new-doc-1',
      name: 'Untitled Document',
      pages: [
        {
          id: 'page-1',
          title: 'Page 1',
          blocks: []
        }
      ]
    },
    ...mockData.documents
  ]);
  const [currentDocumentId, setCurrentDocumentId] = useState('new-doc-1');
  
  // Get current document, page and blocks
  const currentDocument = documents.find(doc => doc.id === currentDocumentId);
  const pages = currentDocument ? currentDocument.pages : [];
  const [currentPageId, setCurrentPageId] = useState(pages[0]?.id || '');
  const currentPage = pages.find(page => page.id === currentPageId);
  const blocks = currentPage ? currentPage.blocks : [];
  
  
  // Ensure currentPageId is valid when document changes
  useEffect(() => {
    if (currentDocument && currentDocument.pages && currentDocument.pages.length > 0) {
      // If current page doesn't exist in the new document, switch to first page
      const pageExists = currentDocument.pages.some(p => p.id === currentPageId);
      if (!pageExists) {
        setCurrentPageId(currentDocument.pages[0].id);
      }
    }
  }, [currentDocumentId, currentDocument, currentPageId]);

  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showPageTemplateDropdown, setShowPageTemplateDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showBottomEmptyBlock, setShowBottomEmptyBlock] = useState(false);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedPage, setDraggedPage] = useState(null);
  const [dragOverPageIndex, setDragOverPageIndex] = useState(null);
  const [showSlashMenu, setShowSlashMenu] = useState(null);
  const [slashMenuSearch, setSlashMenuSearch] = useState('');
  const [editingBlock, setEditingBlock] = useState(null);
  const [selection, setSelection] = useState({ blockId: null, start: null, end: null, rect: null });
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [showEnhancePopup, setShowEnhancePopup] = useState(null);
  const [, setSelectedTemplate] = useState(null);
  const [, setFocusedBlock] = useState(null);
  
  // Block layout state for side-by-side blocks
  const [blockLayouts, setBlockLayouts] = useState({}); // {blockId: {width: percentage, hasRightBlock: boolean}}
  const [resizingBlock, setResizingBlock] = useState(null);
  const [thread, setThread] = useState(undefined);
  const [aiGeneratedBlocks, setAiGeneratedBlocks] = useState(new Set()); // Track which blocks are AI-generated
  const [aiLoadingBlocks, setAiLoadingBlocks] = useState(new Set()); // Track which blocks are currently loading AI content
  const [aiStreamingBlocks, setAiStreamingBlocks] = useState(new Set()); // Track which blocks are actively streaming content
  const [aiOriginalContent, setAiOriginalContent] = useState({}); // Store original AI content for accurate conversion
  const [aiPrompts, setAiPrompts] = useState({}); // Store original AI prompts for each block
  const [editingMarkdown, setEditingMarkdown] = useState({}); // Track which markdown blocks are being edited
  const [showVariablePopup, setShowVariablePopup] = useState(null);
  const [variablePopupPosition, setVariablePopupPosition] = useState({ x: 0, y: 0 });
  const [selectedVariableIndex, setSelectedVariableIndex] = useState(0);
  const [showVariableValues, setShowVariableValues] = useState(false); // Toggle between variable names and values
  // const [originalVariableContent, setOriginalVariableContent] = useState({}); // Store original content with variable names
  const [showToolbarVariablePicker, setShowToolbarVariablePicker] = useState(false); // Toolbar variable picker
  const [toolbarVariableSearch, setToolbarVariableSearch] = useState('');
  const [selectedToolbarVariableIndex, setSelectedToolbarVariableIndex] = useState(0);
  const [savedCursorInfo, setSavedCursorInfo] = useState(null); // Store cursor position and block ID
  const [hasActiveCursor, setHasActiveCursor] = useState(false); // Track if cursor is in a text block
  const [history, setHistory] = useState([]); // Undo/redo history stack
  const [historyIndex, setHistoryIndex] = useState(-1); // Current position in history
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false); // Flag to prevent history recording during undo/redo
  const [isCarouselMode, setIsCarouselMode] = useState(false);
  const [showTemplateSaveDialog, setShowTemplateSaveDialog] = useState(false);
  const [userTemplates, setUserTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [reportBlockExpandedAccounts, setReportBlockExpandedAccounts] = useState({}); // Track expanded accounts in report blocks
  const [showAccountMapping, setShowAccountMapping] = useState(false); // Track account mapping system visibility
  const [showHeadingSettings, setShowHeadingSettings] = useState(null); // Track heading settings dialog
  const [showVideoSettings, setShowVideoSettings] = useState(null); // Track video settings dialog
  const [showChartSettings, setShowChartSettings] = useState(null); // Track chart settings dialog

  // Fetch user templates on mount
  useEffect(() => {
    fetchUserTemplates();
  }, []);

  // Fetch analysis data and report data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch analysis data for AI variables
        const analysisResponse = await axios.get('/api/appData/analysis');
        if (analysisResponse.data) {
          setAnalysisData(analysisResponse.data);
        }

        // Fetch report creator data to replace realApiData
        const reportDataResponse = await axios.get('/api/appData/reportCreatorData');
        if (reportDataResponse.data) {
          setReportApiData(reportDataResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Handle escape key to close template modal
  useEffect(() => {
    if (!showTemplateDropdown) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowTemplateDropdown(false);
        setTemplateSearch('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTemplateDropdown]);

  // Handle escape key to close chart settings modal
  useEffect(() => {
    if (!showChartSettings) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowChartSettings(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showChartSettings]);

  const fetchUserTemplates = async () => {
    try {
      // TODO: Uncomment when backend is ready
      // const response = await axios.get('/api/appData/templates');
      // setUserTemplates(response.data.data || []);
      
      // For now, just use local state
      // Templates will persist in component state during the session
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveAsTemplate = async (templateData) => {
    try {
      const currentDoc = documents.find(d => d.id === currentDocumentId);
      
      if (!currentDoc) {
        throw new Error('No document found to save as template');
      }
      
      // Ensure the document has proper structure with pages and blocks
      const documentToSave = {
        ...currentDoc,
        pages: (currentDoc.pages || [{
          id: 'page-1',
          title: 'Page 1',
          blocks: []
        }]).map(page => ({
          ...page,
          blocks: safeSerializeBlocks(page.blocks || [])
        }))
      };
      
      // For now, stub out the API call and just update local state
      // TODO: Uncomment when backend is ready
      /*
      await axios.post('/api/appData/template', {
        ...templateData,
        documentData: documentToSave
      });
      */
      
      // Create a new template object that matches the expected structure
      const newTemplate = {
        id: `template-${Date.now()}`,
        name: templateData.name,
        description: templateData.description,
        template_data: JSON.stringify(documentToSave),
        created_at: new Date().toISOString(),
        is_public: true
      };
      
      // Add to local templates list
      setUserTemplates([...userTemplates, newTemplate]);
      
      // Also refresh from API when ready
      // await fetchUserTemplates();
      
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  };
  const [editingDocumentId, setEditingDocumentId] = useState(null); // Track which document name is being edited
  

  const authContext = useContext(AuthContext);
  const textRefs = useRef({});
  const formatMenuRef = useRef(null);
  const updateTimeoutRef = useRef({});
  const localContentRef = useRef({}); // Track local content to avoid unnecessary re-renders
  const isTypingRef = useRef({}); // Track which blocks are currently being typed in
  const historyTimeoutRef = useRef({}); // Track debounced history saving for text content

  // Utility: detect chart definition in a string (expects JSON with a 'chart' key)
  function extractChartFromText(text) {
    try {
      const obj = JSON.parse(text);
      if (obj && obj.chart && obj.chart.type && obj.chart.data) {
        console.log('Detected chart:', obj.chart);
        return obj.chart;
      }
    } catch (e) {}
    return null;
  }



  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSlashMenu && !event.target.closest('.slash-menu')) {
        setShowSlashMenu(null);
      }
      if (showEnhancePopup && !event.target.closest('.enhance-popup')) {
        setShowEnhancePopup(null);
      }
      if (showTemplateDropdown && !event.target.closest('.template-dropdown')) {
        setShowTemplateDropdown(false);
      }
      if (showPageTemplateDropdown && !event.target.closest('.page-template-dropdown')) {
        setShowPageTemplateDropdown(false);
      }
      if (formatMenuRef.current && !formatMenuRef.current.contains(event.target)) {
        setShowFormatMenu(false);
      }
      if (showVariablePopup && !event.target.closest('.variable-popup')) {
        setShowVariablePopup(null);
      }
    };

    const handleMouseMove = (e) => {
      if (resizingBlock) {
        handleResizeMove(e);
      }
    };

    const handleMouseUp = () => {
      if (resizingBlock) {
        handleResizeEnd();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Cleanup function to clear any pending timeouts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Copy ref value to avoid stale closure
      const timeouts = updateTimeoutRef.current;
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [showSlashMenu, showEnhancePopup, showTemplateDropdown, showPageTemplateDropdown, showFormatMenu, showVariablePopup, resizingBlock]);



  const loadTemplate = (templateId) => {
    console.log('Loading template:', templateId);
    const template = templates.find(t => t.id === templateId);
    if (!template) {
        console.error(`Template with id ${templateId} not found`);
        console.log('Available templates:', templates.map(t => t.id));
        return;
    }

    const templateBlockPages = templateBlocks[templateId];
    if (!templateBlockPages) {
        console.error(`No blocks found for template with id ${templateId}`);
        console.log('Available templateBlocks:', Object.keys(templateBlocks));
        return;
    }

    console.log('Template blocks found:', templateBlockPages);

    const newDocId = `doc-${Date.now()}`;
    const newDocument = {
        id: newDocId,
        name: template.name,
        pages: templateBlockPages.map((pageTemplate, pageIndex) => ({
            id: `page-${pageIndex + 1}-${Date.now()}`,
            title: pageTemplate.title || `Page ${pageIndex + 1}`,
            blocks: pageTemplate.blocks.map(block => ({
                id: `${block.id}-${Date.now()}-${Math.random()}`,
                ...block
            }))
        }))
    };

    console.log('New document created:', newDocument);

    setDocuments(prev => [...prev, newDocument]);
    setCurrentDocumentId(newDocId);
    setPages(newDocument.pages);
    setCurrentPageId(newDocument.pages[0]?.id || '');
    setSelectedTemplate(templateId);
    setShowTemplateDropdown(false);
  };


  // Get contextual AI prompts for template blocks
  // const getAIPromptForTemplate = (promptKey, templateKey) => {
  //   const promptTemplate = aiTemplatePrompts[promptKey];
  //   if (promptTemplate) {
  //     return promptTemplate.replace('{templateType}', templateKey.replace('-', ' '));
  //   }
  //   return `Generate relevant content for ${promptKey} in the context of a ${templateKey.replace('-', ' ')}.`;
  // };


  const addPage = (templateId, insertAfterPageId = null) => {
    const template = pageTemplates.find(t => t.id === templateId);
    if (!template) return;

    const newPageId = `page-${Date.now()}`;
    const newPage = {
      id: newPageId,
      title: template.name,
      blocks: template.blocks.map(block => ({
        ...block,
        id: `${block.id}-${Date.now()}-${Math.random()}`
      }))
    };

    setPages(prevPages => {
      let newPages;
      if (insertAfterPageId) {
        const insertIndex = prevPages.findIndex(p => p.id === insertAfterPageId) + 1;
        newPages = [...prevPages];
        newPages.splice(insertIndex, 0, newPage);
      } else {
        const currentIndex = prevPages.findIndex(p => p.id === currentPageId);
        const insertIndex = currentIndex + 1;
        newPages = [...prevPages];
        newPages.splice(insertIndex, 0, newPage);
      }
      return newPages;
    });
    setCurrentPageId(newPageId);
    setShowPageTemplateDropdown(false);
  };

  const deletePage = (pageId) => {
    if (pages.length <= 1) return; // Don't delete the last page
    
    setDocuments(prevDocs => 
      prevDocs.map(doc => 
        doc.id === currentDocumentId 
          ? { ...doc, pages: doc.pages.filter(p => p.id !== pageId) }
          : doc
      )
    );
    
    if (currentPageId === pageId) {
      const remainingPages = pages.filter(p => p.id !== pageId);
      setCurrentPageId(remainingPages[0]?.id || '');
    }
  };

  const updatePage = (pageId, updates) => {
    setDocuments(prevDocs => 
      prevDocs.map(doc => 
        doc.id === currentDocumentId 
          ? {
              ...doc,
              pages: doc.pages.map(page => 
                page.id === pageId ? { ...page, ...updates } : page
              )
            }
          : doc
      )
    );
  };

  const addBlock = (type, index = null, content = null) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;
    
    const actualIndex = index !== null ? index : currentPage.blocks.length;
    
    // Save to history before making changes
    saveToHistory('add_block', `Added ${type} block`);
    
    const newBlock = {
      id: Date.now().toString(),
      type,
      content: content || getDefaultContent(type)
    };
    const newBlocks = [...currentPage.blocks];
    newBlocks.splice(actualIndex, 0, newBlock);
    updatePage(currentPageId, { blocks: newBlocks });
    setShowSlashMenu(null);
    // Note: Focus handling is done by caller if needed
    return newBlock.id;
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'text':
        return { text: '' };
      case 'title':
        return { text: 'Untitled' };
      case 'heading':
        return { text: 'New Heading', level: 2 };
      case 'kpi':
        return {
          title: 'Key Metrics',
          metrics: [
            { label: 'Metric 1', value: '$0', change: '0%', trend: 'neutral' }
          ]
        };
      case 'chart':
        return {
          title: 'Chart Title',
          chartType: 'bar',
          data: [] // Ensure data is initialized as an array
        };
      case 'table':
        return {
          title: 'Data Table',
          headers: ['Column 1', 'Column 2'],
          rows: [['Value 1', 'Value 2']]
        };
      case 'quote':
        return { text: 'Add your quote here...', author: '' };
      case 'divider':
        return {};
      case 'financial-summary':
        return {
          kpis: [
            { label: 'Cash on Hand', value: '$120,000', change: '+5%', trend: 'up' },
            { label: 'Revenue', value: '$45,230', change: '+12.5%', trend: 'up' },
            { label: 'Burn Rate', value: '$18,000', change: '-2%', trend: 'down' }
          ],
          lineChart: {
            title: 'Revenue vs Expenses',
            chartType: 'line',
            data: [
              { month: 'Oct', revenue: 35000, expenses: 15000 },
              { month: 'Nov', revenue: 40000, expenses: 17000 },
              { month: 'Dec', revenue: 45230, expenses: 18000 }
            ]
          },
          pieChart: {
            title: 'Expenses by Account',
            chartType: 'pie',
            data: [
              { name: 'Payroll', value: 9000 },
              { name: 'Rent', value: 4000 },
              { name: 'Software', value: 2500 },
              { name: 'Marketing', value: 2500 }
            ]
          }
        };
      case 'reportBlock':
        return {
          reportType: 'profit-loss',
          title: 'Profit & Loss Statement',
          dateRange: 'December 2024',
          data: {
            summary: {
              totalRevenue: reportApiData?.revenue || 0,
              totalExpenses: reportApiData?.expenses || 0,
              netIncome: reportApiData?.netIncome || 0
            },
            accounts: reportApiData?.accounts || []
          }
        };
      case 'video':
        return {
          title: 'Video',
          url: '',
          embedUrl: '',
          platform: '' // 'loom', 'youtube', 'vimeo', 'other'
        };
      case 'ai':
        return {
          prompt: '',
          text: ''
        };
      default:
        return {};
    }
  };


  const updateBlockContent = (blockId, newContent) => {
    setPages(prevPages => 
      prevPages.map(page => 
        page.id === currentPageId 
          ? {
              ...page,
              blocks: page.blocks.map(block => 
                block.id === blockId 
                  ? { ...block, content: { ...block.content, ...newContent } }
                  : block
              )
            }
          : page
      )
    );

    // Debounced history saving for text content (wait 1 second after user stops typing)
    if (newContent.text !== undefined) {
      if (historyTimeoutRef.current[blockId]) {
        clearTimeout(historyTimeoutRef.current[blockId]);
      }
      
      historyTimeoutRef.current[blockId] = setTimeout(() => {
        const currentPage = pages.find(p => p.id === currentPageId);
        const block = currentPage?.blocks.find(b => b.id === blockId);
        saveToHistory('edit_content', `Edited ${block?.type || 'text'} content`);
        delete historyTimeoutRef.current[blockId];
      }, 1000);
    }
  };

  const removeBlock = (id) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage || currentPage.blocks.length === 1) return;
    
    // Save to history before making changes
    const block = currentPage.blocks.find(b => b.id === id);
    saveToHistory('remove_block', `Removed ${block?.type || 'unknown'} block`);
    
    const newBlocks = currentPage.blocks.filter(block => block.id !== id);
    updatePage(currentPageId, { blocks: newBlocks });
    setFocusedBlock(null);
  };

  const duplicateBlock = (block, index) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;
    
    // Save to history before making changes
    saveToHistory('duplicate_block', `Duplicated ${block.type} block`);
    
    let newBlock;
    
    if (block.type === 'chart') {
      // For chart blocks, only clone the essential settings, not the chart data
      newBlock = {
        id: Date.now().toString(),
        type: 'chart',
        content: {
          title: block.content?.title || '',
          chartType: block.content?.chartType || 'bar',
          // Store the chart configuration separately from the rendered data
          chartTitle: block.content?.chartTitle,
          queryOptions: block.content?.queryOptions ? JSON.parse(JSON.stringify(block.content.queryOptions)) : undefined,
          targetValue: block.content?.targetValue,
          color: block.content?.color,
          // Don't clone the actual chart data - it will be regenerated when settings are opened
          data: null
        }
      };
    } else {
      // For non-chart blocks, do a simple clone
      try {
        newBlock = {
          ...JSON.parse(JSON.stringify(block)),
          id: Date.now().toString(),
        };
      } catch (error) {
        // Fallback for any other serialization issues
        newBlock = {
          id: Date.now().toString(),
          type: block.type,
          content: { ...block.content }
        };
      }
    }
    
    const newBlocks = [...currentPage.blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    updatePage(currentPageId, { blocks: newBlocks });
  };

  const moveBlock = (fromIndex, toIndex) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;
    
    // Save to history before making changes
    saveToHistory('move_block', 'Moved block');
    
    const newBlocks = [...currentPage.blocks];
    const [removed] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, removed);
    updatePage(currentPageId, { blocks: newBlocks });
  };

  const movePage = (fromIndex, toIndex) => {
    const newPages = [...pages];
    const [removed] = newPages.splice(fromIndex, 1);
    newPages.splice(toIndex, 0, removed);
    setPages(newPages);
  };


  const handleTextInput = (blockId, text) => {
    // Get previous text before updating
    const previousText = localContentRef.current[blockId] || '';
    
    // Store the content locally and mark as actively typing
    localContentRef.current[blockId] = text;
    isTypingRef.current[blockId] = true;
    
    // Clear any existing timeout for this block
    if (updateTimeoutRef.current[blockId]) {
      clearTimeout(updateTimeoutRef.current[blockId]);
    }
    
    // Only update React state after user stops typing (debounced)
    updateTimeoutRef.current[blockId] = setTimeout(() => {
      updateBlockContent(blockId, { text });
      isTypingRef.current[blockId] = false; // Mark as no longer typing
      delete updateTimeoutRef.current[blockId];
    }, 500);
    
    // Handle special commands immediately for responsiveness
    if (text.endsWith('/')) {
      const blockIndex = blocks.findIndex(b => b.id === blockId);
      setShowSlashMenu({ blockId, index: blockIndex });
      setShowVariablePopup(null);
    } 
    else if (text.includes('{{')) {
      // Check if user just typed {{ by comparing with previous content
      const doubleBraceCount = (text.match(/\{\{/g) || []).length;
      const previousDoubleBraceCount = (previousText.match(/\{\{/g) || []).length;
      
      if (doubleBraceCount > previousDoubleBraceCount) {
        // User just added a new {{ - show variable popup
        const textRef = textRefs.current[blockId];
        if (textRef) {
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const position = { x: rect.left, y: rect.bottom + 5 };
            setVariablePopupPosition(position);
            setShowVariablePopup({ blockId });
            setSelectedVariableIndex(0);
          } else {
            // Fallback positioning if no selection
            const rect = textRef.getBoundingClientRect();
            const position = { x: rect.left, y: rect.bottom + 5 };
            setVariablePopupPosition(position);
            setShowVariablePopup({ blockId });
            setSelectedVariableIndex(0);
          }
        }
        setShowSlashMenu(null);
      }
    }
    else {
      setShowSlashMenu(null);
      setShowVariablePopup(null);
    }
  };


  const handleSlashMenuSelect = (type) => {
    if (!showSlashMenu) return;
    const { blockId, index } = showSlashMenu;
    const currentBlock = blocks.find(b => b.id === blockId);
    if (currentBlock && currentBlock.content.text === '/') {
      // Update block type and content
      const newBlocks = blocks.map(block =>
        block.id === blockId ? { ...block, type, content: getDefaultContent(type) } : block
      );
      updatePage(currentPageId, { blocks: newBlocks });
    } else if (blockId === 'empty-block') {
      // Handle empty block case - create new block and clear the empty block
      addBlock(type, index);
      // Clear the empty block content
      const emptyBlockEl = textRefs.current['empty-block'];
      if (emptyBlockEl) {
        emptyBlockEl.innerHTML = '';
      }
      setShowBottomEmptyBlock(false);
    } else {
      addBlock(type, index + 1);
    }
    setShowSlashMenu(null);
    setSlashMenuSearch(''); // Clear search when menu closes
    
    // Additional cleanup for empty block
    const emptyBlockEl = textRefs.current['empty-block'];
    if (emptyBlockEl) {
      emptyBlockEl.innerHTML = '';
    }
  };

  const handleAIPromptSubmit = async (promptOrBlockId, optionalPrompt) => {
    let prompt, blockId, index;
    
    if (typeof promptOrBlockId === 'string' && optionalPrompt) {
      // Called from AI block: handleAIGeneration(blockId, prompt)
      blockId = promptOrBlockId;
      prompt = optionalPrompt;
      index = 0; // Not used for existing blocks
    } else {
      // Called from old interface or direct calls: handleAIPromptSubmit(prompt)
      prompt = promptOrBlockId;
      if (!prompt || !prompt.trim()) return;
      
      // Create a new block at the end of the document
      blockId = 'empty-block';
      index = pages.find(p => p.id === currentPageId)?.blocks?.length || 0;
    }
    
    if (!prompt || !prompt.trim()) return;
    
    // Handle the empty block case - create a new block first
    let targetBlockId = blockId;
    if (blockId === 'empty-block') {
      targetBlockId = addBlock('text', index, { text: '' });
      // Clear the empty block content
      const emptyBlockEl = textRefs.current['empty-block'];
      if (emptyBlockEl) {
        emptyBlockEl.innerHTML = '';
      }
      setShowBottomEmptyBlock(false);
      // Mark as AI-generated and loading
      setAiGeneratedBlocks(prev => new Set([...prev, targetBlockId]));
      setAiLoadingBlocks(prev => new Set([...prev, targetBlockId]));
      // Store the original prompt
      setAiPrompts(prev => ({ ...prev, [targetBlockId]: prompt }));
    } else {
      // Check if this is an AI block - if so, keep it as AI block and update prompt
      const currentPage = pages.find(p => p.id === currentPageId);
      const block = currentPage?.blocks?.find(b => b.id === blockId);
      
      if (block && block.type === 'ai') {
        // Update AI block content with new prompt and clear generated text for regeneration
        updateBlockContent(blockId, { prompt, text: '' });
        targetBlockId = blockId;
      } else {
        // Convert existing block to text and clear content
        updateBlockContent(blockId, { text: '' });
        targetBlockId = blockId;
      }
      
      // Save current state to history before regenerating content
      saveToHistory('regenerate_ai', `Regenerated AI content`);
      
      // Mark as AI-generated and loading
      setAiGeneratedBlocks(prev => new Set([...prev, targetBlockId]));
      setAiLoadingBlocks(prev => new Set([...prev, targetBlockId]));
      // Store the original prompt
      setAiPrompts(prev => ({ ...prev, [targetBlockId]: prompt }));
    }
    
    
    // Additional cleanup for empty block
    const emptyBlockEl = textRefs.current['empty-block'];
    if (emptyBlockEl) {
      emptyBlockEl.innerHTML = '';
    }
    
    try {
      // Prepare the API request data (same as chat.js)
      const data = {
        headers: {
          responseType: 'stream'
        },
        app_connection_id: authContext.user.app_connection_id,
        message: prompt,
        thread: thread
      };
      
      const headers = {
        ...axios.defaults.headers.common,
        'Content-Type': 'application/json',
        responseType: 'stream'
      };
      
      const res = await fetch('/api/chatai', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data)
      });

      // Process as streaming response
      if (res.ok && res.body) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = "";
        let currMessage = "";
        
        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                console.log('Stream completed');
                break;
              }
              
              const chunkText = decoder.decode(value, { stream: true });
              console.log('Received chunk:', chunkText);
              accumulatedText += chunkText;
              
              // Check if accumulated text contains a chart (check complete accumulated data)
              const chartObj = extractChartFromText(accumulatedText);
              if (chartObj) {
                // Replace the text block with a chart block
                const chartTitle = chartObj.title || chartObj.chartTitle || chartObj.data?.title || 
                  (chartObj.type ? (chartObj.type.charAt(0).toUpperCase() + chartObj.type.slice(1) + ' Chart') : 'Chart');
                
                setPages(prevPages => 
                  prevPages.map(page => 
                    page.id === currentPageId 
                      ? {
                          ...page,
                          blocks: page.blocks.map(block => 
                            block.id === targetBlockId 
                              ? { 
                                  ...block, 
                                  type: 'chart', 
                                  content: { 
                                    title: chartTitle,
                                    chartType: chartObj.type,
                                    data: chartObj.data,
                                    options: chartObj.options
                                  } 
                                } 
                              : block
                          )
                        }
                      : page
                  )
                );
                return;
              }
              
              // Handle concatenated JSON objects like {"text":"The"}{"text":"The business"}
              while (accumulatedText.length > 0) {
                // Find the start of a JSON object
                const jsonStart = accumulatedText.indexOf('{');
                if (jsonStart === -1) {
                  accumulatedText = '';
                  break;
                }
                
                // Remove any text before the JSON object
                if (jsonStart > 0) {
                  accumulatedText = accumulatedText.substring(jsonStart);
                }
                
                // Find the matching closing brace
                let braceCount = 0;
                let jsonEnd = -1;
                
                for (let i = 0; i < accumulatedText.length; i++) {
                  if (accumulatedText[i] === '{') {
                    braceCount++;
                  } else if (accumulatedText[i] === '}') {
                    braceCount--;
                    if (braceCount === 0) {
                      jsonEnd = i;
                      break;
                    }
                  }
                }
                
                if (jsonEnd === -1) {
                  // Incomplete JSON object, wait for more data
                  break;
                }
                
                // Extract and parse the complete JSON object
                const jsonStr = accumulatedText.substring(0, jsonEnd + 1);
                
                try {
                  const json = JSON.parse(jsonStr);
                  console.log('Parsed JSON:', json, 'from string:', jsonStr);
                  
                  if (json.thread !== undefined && thread === undefined) {
                    setThread(json.thread);
                  }
                  
                  if (json.text !== undefined) {
                    currMessage = json.text;
                    const currentMessageText = currMessage; // Create local copy to avoid closure issues
                    console.log('Updating block with text:', currentMessageText);
                    // Remove from loading state and add to streaming state on first text update
                    setAiLoadingBlocks(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(targetBlockId);
                      return newSet;
                    });
                    setAiStreamingBlocks(prev => new Set([...prev, targetBlockId]));
                    // Store the original AI content for accurate formatting conversion
                    setAiOriginalContent(prev => ({
                      ...prev,
                      [targetBlockId]: currentMessageText
                    }));
                    // Update the target block content with streaming text
                    setPages(prevPages => 
                      prevPages.map(page => 
                        page.id === currentPageId 
                          ? {
                              ...page,
                              blocks: page.blocks.map(block => 
                                block.id === targetBlockId 
                                  ? { ...block, content: { ...block.content, text: currentMessageText } }
                                  : block
                              )
                            }
                          : page
                      )
                    );
                  }
                } catch (parseError) {
                  console.log('Failed to parse JSON:', jsonStr, parseError);
                }
                
                // Move to the next potential JSON object
                accumulatedText = accumulatedText.substring(jsonEnd + 1);
              }
            }
            
            // Stream completed - remove from streaming state after a delay to show final cursor
            setTimeout(() => {
              setAiStreamingBlocks(prev => {
                const newSet = new Set(prev);
                newSet.delete(targetBlockId);
                return newSet;
              });
            }, 2000);
            
            // Process any remaining data
            if (accumulatedText.trim()) {
              const jsonStart = accumulatedText.indexOf('{');
              if (jsonStart !== -1) {
                try {
                  const json = JSON.parse(accumulatedText.substring(jsonStart));
                  if (json.text !== undefined) {
                    currMessage = json.text;
                    // Remove from loading state and add to streaming state on final text update
                    setAiLoadingBlocks(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(targetBlockId);
                      return newSet;
                    });
                    setAiStreamingBlocks(prev => new Set([...prev, targetBlockId]));
                    // Store the original AI content for accurate formatting conversion
                    setAiOriginalContent(prev => ({
                      ...prev,
                      [targetBlockId]: currMessage
                    }));
                    setPages(prevPages => 
                      prevPages.map(page => 
                        page.id === currentPageId 
                          ? {
                              ...page,
                              blocks: page.blocks.map(block => 
                                block.id === targetBlockId 
                                  ? { ...block, content: { ...block.content, text: currMessage } }
                                  : block
                              )
                            }
                          : page
                      )
                    );
                  }
                } catch (parseError) {
                  console.log('Failed to parse final chunk:', accumulatedText, parseError);
                }
              }
            }
          } catch (streamError) {
            console.error('Stream processing error:', streamError);
            throw streamError;
          }
        };
        
        await processStream();
      } else {
        console.error('Error:', res.status, res.statusText);
        // Handle API failure
        setTimeout(() => {
          setAiLoadingBlocks(prev => {
            const newSet = new Set(prev);
            newSet.delete(targetBlockId);
            return newSet;
          });
          setAiStreamingBlocks(prev => {
            const newSet = new Set(prev);
            newSet.delete(targetBlockId);
            return newSet;
          });
          setAiOriginalContent(prev => {
            const newContent = { ...prev };
            delete newContent[targetBlockId];
            return newContent;
          });
          updateBlockContent(targetBlockId, { text: 'Error: Unable to generate AI content. Please try again later.' });
        }, 1500);
      }
    } catch (error) {
      console.error('Error calling AI API:', error);
      // Handle error
      setTimeout(() => {
        setAiLoadingBlocks(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetBlockId);
          return newSet;
        });
        setAiStreamingBlocks(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetBlockId);
          return newSet;
        });
        setAiOriginalContent(prev => {
          const newContent = { ...prev };
          delete newContent[targetBlockId];
          return newContent;
        });
        updateBlockContent(targetBlockId, { text: 'Error: Unable to generate AI content. Please try again later.' });
      }, 1500);
    }
  };

  // Handler for AI settings button
  const handleAISettingsClick = (blockId) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    const block = currentPage?.blocks?.find(b => b.id === blockId);
    
    if (!block) return;
    
    // Handle different block types
    if (block.type === 'heading') {
      setShowHeadingSettings(blockId);
    } else if (block.type === 'video') {
      setShowVideoSettings(blockId);
    } else if (block.type === 'chart') {
      setShowChartSettings(blockId);
    } else {
      // For text blocks, convert to AI block and populate with saved prompt
      const currentPage = pages.find(p => p.id === currentPageId);
      const block = currentPage?.blocks?.find(b => b.id === blockId);
      
      if (block) {
        const savedPrompt = aiPrompts[blockId] || '';
        // Convert block to AI type and populate prompt
        updateBlockContent(blockId, { 
          prompt: savedPrompt,
          text: block.content?.text || ''
        });
        // Update block type to AI
        const newBlocks = currentPage.blocks.map(b =>
          b.id === blockId ? { ...b, type: 'ai' } : b
        );
        updatePage(currentPageId, { blocks: newBlocks });
      }
      setShowSlashMenu(null);
    }
  };

  // Close toolbar variable picker on Escape or click outside
  useEffect(() => {
    if (!showToolbarVariablePicker) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowToolbarVariablePicker(false);
        setToolbarVariableSearch('');
        setSelectedToolbarVariableIndex(0);
        return;
      }

      const filteredVariables = availableVariables.filter(variable => 
        variable.name.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
        variable.label.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
        variable.description.toLowerCase().includes(toolbarVariableSearch.toLowerCase())
      );

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedToolbarVariableIndex(prev => 
          prev < filteredVariables.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedToolbarVariableIndex(prev => 
          prev > 0 ? prev - 1 : filteredVariables.length - 1
        );
      } else if (e.key === 'Enter' && filteredVariables.length > 0) {
        e.preventDefault();
        insertVariableAtCursor(filteredVariables[selectedToolbarVariableIndex].name);
      }
    };

    const handleClickOutside = (e) => {
      const picker = document.querySelector('.toolbar-variable-picker');
      if (picker && !picker.contains(e.target)) {
        setShowToolbarVariablePicker(false);
        setToolbarVariableSearch('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolbarVariablePicker, selectedToolbarVariableIndex, toolbarVariableSearch]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedToolbarVariableIndex(0);
  }, [toolbarVariableSearch]);

  // Check if cursor is in a text block for button state
  const checkActiveCursor = () => {
    const cursorInfo = saveCursorPosition();
    setHasActiveCursor(!!cursorInfo);
    return cursorInfo;
  };

  // Listen for selection changes to update button state
  useEffect(() => {
    const handleSelectionChange = () => {
      // Debounce the check to avoid excessive updates
      clearTimeout(window.cursorCheckTimeout);
      window.cursorCheckTimeout = setTimeout(() => {
        checkActiveCursor();
      }, 100);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('click', handleSelectionChange);
    document.addEventListener('keyup', handleSelectionChange);

    // Initial check
    checkActiveCursor();

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('click', handleSelectionChange);
      document.removeEventListener('keyup', handleSelectionChange);
      clearTimeout(window.cursorCheckTimeout);
    };
  }, []);

  // Variable System Functions
  
  // Helper function to calculate financial data from GL accounts
  const calculateFromAccounts = () => {
    const accounts = (reportApiData?.accounts) || [];
    
    // Calculate revenue from Income accounts
    const revenue = accounts
      .filter(acc => acc.type === 'Income')
      .reduce((sum, acc) => sum + (acc.p_l || 0), 0);
    
    // Calculate COGS
    const cogs = accounts
      .filter(acc => acc.type === 'Cost of Goods Sold')
      .reduce((sum, acc) => sum + Math.abs(acc.p_l || 0), 0);
    
    // Calculate expenses from Expense accounts
    const expenses = accounts
      .filter(acc => acc.type === 'Expense' || acc.type === 'Other Expense')
      .reduce((sum, acc) => sum + Math.abs(acc.p_l || 0), 0);
    
    // Calculate cash from Bank accounts
    const cash = accounts
      .filter(acc => acc.type === 'Bank')
      .reduce((sum, acc) => sum + (acc.val || 0), 0);
    
    return {
      revenue: revenue || reportApiData?.revenue || 0,
      cogs: cogs || reportApiData?.cogs || 0,
      expenses: expenses || reportApiData?.expenses || 0,
      cash: cash || reportApiData?.cashOnHand || 0,
      netIncome: (revenue - cogs - expenses) || reportApiData?.netIncome || 0
    };
  };

  const getVariableValue = (variableName) => {
    const currentDate = new Date();
    const calculated = calculateFromAccounts();
    
    // Helper function to calculate percentage change
    const calculatePercentChange = (current, prior) => {
      if (!prior || prior === 0) return '0.0%';
      const change = ((current - prior) / Math.abs(prior)) * 100;
      return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    };
    
    switch (variableName) {
      // Financial Metrics (using calculated values from GL accounts when available)
      case 'REVENUE':
        return `$${calculated.revenue.toLocaleString()}`;
      case 'EXPENSES':
        return `$${calculated.expenses.toLocaleString()}`;
      case 'NET_INCOME':
        return `$${calculated.netIncome.toLocaleString()}`;
      case 'GROSS_PROFIT':
        const grossProfit = calculated.revenue - calculated.cogs;
        return `$${grossProfit.toLocaleString()}`;
      case 'GROSS_MARGIN':
        const margin = calculated.revenue > 0 ? ((calculated.revenue - calculated.cogs) / calculated.revenue * 100).toFixed(1) : '0.0';
        return `${margin}%`;
      case 'CASH_ON_HAND':
        return `$${calculated.cash.toLocaleString()}`;
      
      // Period Comparisons (calculated from actual data)
      case 'REVENUE_CHANGE':
        return calculatePercentChange(calculated.revenue, reportApiData?.priorRevenue || 0);
      case 'INCOME_CHANGE':
        return calculatePercentChange(calculated.netIncome, reportApiData?.priorNetIncome || 0);
      case 'CASH_CHANGE':
        return calculatePercentChange(calculated.cash, reportApiData?.priorCashOnHand || 0);
      case 'MARGIN_CHANGE':
        const currentMargin = calculated.revenue > 0 ? ((calculated.revenue - calculated.cogs) / calculated.revenue * 100) : 0;
        const priorMargin = (reportApiData?.priorRevenue || 0) > 0 ? 
          (((reportApiData?.priorRevenue || 0) - (reportApiData?.priorCogs || 0)) / (reportApiData?.priorRevenue || 0) * 100) : 0;
        const marginDiff = currentMargin - priorMargin;
        return `${marginDiff >= 0 ? '+' : ''}${marginDiff.toFixed(1)}%`;
      
      // Company Information
      case 'COMPANY_NAME':
        // Try to get from context first, then fallback to API data
        return authContext?.account?.name || reportApiData?.companyName || 'Sample Company Inc.';
      case 'PREPARED_BY':
        // Try to get from firm context first
        return authContext?.firms?.[0]?.name || reportApiData?.preparedBy || 'Your Accounting Firm';
      
      // Date/Time Variables
      case 'CURRENT_DATE':
        return currentDate.toLocaleDateString();
      case 'REPORT_DATE':
        return currentDate.toLocaleDateString();
      case 'REPORT_PERIOD':
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'CURRENT_MONTH':
        return currentDate.toLocaleDateString('en-US', { month: 'long' });
      case 'CURRENT_YEAR':
        return currentDate.getFullYear().toString();
      case 'CURRENT_QUARTER':
        const quarter = Math.floor((currentDate.getMonth() + 3) / 3);
        return `Q${quarter}`;
      case 'MONTH_END_DATE':
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        return monthEnd.toLocaleDateString();
      case 'NEXT_WEEK_DATE':
        const nextWeek = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        return nextWeek.toLocaleDateString();
      
      // AI Business Analysis Variables
      case 'AI_SUMMARY':
        return analysisData?.summary || 'No AI analysis available';
      case 'AI_OPPORTUNITIES':
        if (analysisData?.opportunities?.length > 0) {
          return analysisData.opportunities.map((opp, i) => `${i + 1}. ${opp}`).join('\n');
        }
        return 'No opportunities identified';
      case 'AI_RISKS':
        if (analysisData?.risks?.length > 0) {
          return analysisData.risks.map((risk, i) => `${i + 1}. ${risk}`).join('\n');
        }
        return 'No risks identified';
      case 'AI_ANOMALIES':
        if (analysisData?.anomalies?.length > 0) {
          return analysisData.anomalies.map((anomaly, i) => `${i + 1}. ${anomaly}`).join('\n');
        }
        return 'No anomalies detected';
      case 'CASH_FLOW_OUTLOOK':
        const cashFlowColors = { red: '🔴 Critical', yellow: '🟡 Caution', green: '🟢 Healthy' };
        return cashFlowColors[analysisData?.cash_flow_outlook] || '🟡 Unknown';
      case 'OVERALL_HEALTH':
        const healthColors = { red: '🔴 Poor', yellow: '🟡 Fair', green: '🟢 Good' };
        return healthColors[analysisData?.overall_health] || '🟡 Unknown';
      
      default:
        return `{{${variableName}}}`;
    }
  };

  const renderTextWithVariables = (text, mode = 'edit') => {
    if (!text) return '';
    
    if (mode === 'edit') {
      // In edit mode, always show blue highlighting to indicate variables
      if (!showVariableValues) {
        // Show variable names with light blue background styling
        return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
          return `<span style="background-color: #dbeafe; color: #1e40af; padding: 1px 4px; border-radius: 3px; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap; max-width: 100%; display: inline; line-height: inherit;">${match}</span>`;
        });
      } else {
        // Show substituted values with light blue background styling
        return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
          const value = getVariableValue(variableName);
          // Escape HTML in the value to prevent injection and preserve line breaks
          const escapedValue = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
          return `<span style="background-color: #dbeafe; color: #1e40af; padding: 1px 4px; border-radius: 3px; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap; max-width: 100%; display: inline; line-height: inherit;">${escapedValue}</span>`;
        });
      }
    }
    
    // View/carousel mode: Show clean substituted values without highlighting
    return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
      const value = getVariableValue(variableName);
      // In view mode, never show variable names - if variable not found, show empty string
      // Preserve line breaks in view mode by replacing \n with actual line breaks
      const formattedValue = value === `{{${variableName}}}` ? '' : value.replace(/\n/g, '\n');
      return formattedValue;
    });
  };

  // Toggle between showing variable names and values in edit mode
  const toggleVariableDisplay = () => {
    setShowVariableValues(!showVariableValues);
  };

  // Trigger layout recalculation when variable display changes or blocks change
  useEffect(() => {
    // Force a reflow to ensure page height adjusts to content changes
    const timer = setTimeout(() => {
      // Trigger reflow by briefly modifying a style property
      const pages = document.querySelectorAll('[class*="reportPaper"]');
      pages.forEach(page => {
        const currentDisplay = page.style.display;
        page.style.display = 'none';
        // Force a reflow by accessing offsetHeight
        void page.offsetHeight; // eslint-disable-line no-void
        page.style.display = currentDisplay || 'flex';
      });
    }, 50); // Small delay to ensure content has updated

    return () => clearTimeout(timer);
  }, [showVariableValues, blocks]);

  // Save current cursor position and block info for toolbar insertion
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const focusNode = range.startContainer;
    
    // Find the block that contains the cursor
    let blockElement = focusNode;
    while (blockElement && !blockElement.getAttribute) {
      blockElement = blockElement.parentNode;
    }
    
    // Look for contentEditable element
    while (blockElement && !blockElement.contentEditable && !blockElement.getAttribute('data-block-id')) {
      blockElement = blockElement.parentNode;
    }
    
    if (!blockElement) return null;
    
    // Find the block ID
    let blockId = null;
    for (const [id, ref] of Object.entries(textRefs.current)) {
      if (ref === blockElement || ref?.contains(blockElement)) {
        blockId = id;
        break;
      }
    }
    
    if (!blockId) return null;

    // Calculate text offset position
    let textOffset = 0;
    const walker = document.createTreeWalker(
      blockElement,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    while ((node = walker.nextNode())) {
      if (node === range.startContainer) {
        textOffset += range.startOffset;
        break;
      } else {
        textOffset += node.textContent.length;
      }
    }

    return { blockId, textOffset, blockElement };
  };

  // Insert variable at current cursor position from toolbar
  const insertVariableAtCursor = (variableName) => {
    if (!savedCursorInfo) {
      console.warn('No saved cursor position for variable insertion');
      return;
    }

    const { blockId, textOffset } = savedCursorInfo;
    const textRef = textRefs.current[blockId];
    if (!textRef) {
      console.warn('Text ref not found for block:', blockId);
      return;
    }

    // Get current text content
    const currentText = textRef.innerText || textRef.textContent || '';
    
    // Insert variable at the saved position
    const beforeText = currentText.substring(0, textOffset);
    const afterText = currentText.substring(textOffset);
    const variableText = `{{${variableName}}} `;
    const newText = beforeText + variableText + afterText;
    
    // Update block content
    updateBlockContent(blockId, { text: newText });
    
    // Close the picker
    setShowToolbarVariablePicker(false);
    setToolbarVariableSearch('');
    setSavedCursorInfo(null);
    
    // Focus back to the text element and position cursor after variable
    setTimeout(() => {
      textRef.focus();
      const newCursorPosition = beforeText.length + variableText.length;
      
      // Set cursor position
      const selection = window.getSelection();
      const range = document.createRange();
      
      let currentPos = 0;
      const walker = document.createTreeWalker(
        textRef,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      while ((node = walker.nextNode())) {
        const nodeLength = node.textContent.length;
        if (currentPos + nodeLength >= newCursorPosition) {
          const offsetInNode = newCursorPosition - currentPos;
          range.setStart(node, Math.min(offsetInNode, nodeLength));
          range.setEnd(node, Math.min(offsetInNode, nodeLength));
          break;
        }
        currentPos += nodeLength;
      }
      
      if (range.startContainer) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 100);
  };

  // OLD insertVariableAtCursor function - keeping as backup

  const insertVariable = (blockId, variableName) => {
    const textRef = textRefs.current[blockId];
    if (!textRef) return;

    // Simple approach: get current text and replace {{ with {{VARIABLE}} 
    const currentText = textRef.innerText || textRef.textContent || '';
    const lastDoubleOpenBrace = currentText.lastIndexOf('{{');
    
    if (lastDoubleOpenBrace !== -1) {
      // Replace the {{ with the complete variable placeholder
      const beforeVariable = currentText.substring(0, lastDoubleOpenBrace);
      const afterVariable = currentText.substring(lastDoubleOpenBrace + 2); // Skip the {{
      
      const variableText = `{{${variableName}}} `; // Add space after variable
      const newText = beforeVariable + variableText + afterVariable;
      
      // Update the block content - this will trigger re-render with variable highlighting
      updateBlockContent(blockId, { text: newText });
      
      // Calculate where cursor should be (after the inserted variable)
      const cursorPosition = beforeVariable.length + variableText.length;
      
      // Set cursor position after the variable
      setTimeout(() => {
        textRef.focus();
        
        // Position cursor at the calculated position
        const selection = window.getSelection();
        const range = document.createRange();
        
        // Find the text node and set cursor position
        let currentPos = 0;
        const walker = document.createTreeWalker(
          textRef,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let node;
        while ((node = walker.nextNode())) {
          const nodeLength = node.textContent.length;
          if (currentPos + nodeLength >= cursorPosition) {
            // Found the node containing our cursor position
            const offsetInNode = cursorPosition - currentPos;
            range.setStart(node, Math.min(offsetInNode, nodeLength));
            range.setEnd(node, Math.min(offsetInNode, nodeLength));
            break;
          }
          currentPos += nodeLength;
        }
        
        if (range.startContainer) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 100);
    }
    
    // Hide variable popup
    setShowVariablePopup(null);
  };

  // Safe serialization for blocks that handles chart data
  const safeSerializeBlocks = (blocks) => {
    return blocks.map(block => {
      if (block.type === 'chart') {
        // For chart blocks, only serialize essential properties
        return {
          id: block.id,
          type: block.type,
          content: {
            title: block.content?.title || '',
            chartType: block.content?.chartType || 'bar',
            chartTitle: block.content?.chartTitle,
            queryOptions: block.content?.queryOptions,
            targetValue: block.content?.targetValue,
            color: block.content?.color,
            // Don't serialize the actual chart data
            data: null
          }
        };
      }
      // For other blocks, try normal serialization with fallback
      try {
        return JSON.parse(JSON.stringify(block));
      } catch (error) {
        console.warn('Error serializing block, using basic copy:', error);
        return {
          id: block.id,
          type: block.type,
          content: block.content ? { ...block.content } : {}
        };
      }
    });
  };

  // Undo/Redo functionality
  const saveToHistory = (action, description) => {
    if (isUndoRedoAction) return; // Don't save during undo/redo operations

    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;

    const snapshot = {
      action,
      description,
      timestamp: Date.now(),
      pageId: currentPageId,
      pageState: {
        blocks: safeSerializeBlocks(currentPage.blocks),
        title: currentPage.title
      },
      // Also save relevant AI-related state
      aiGeneratedBlocks: new Set(aiGeneratedBlocks),
      aiPrompts: { ...aiPrompts },
      aiOriginalContent: { ...aiOriginalContent }
    };

    setHistory(prev => {
      // Remove any history after current index (when user makes new change after undo)
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(snapshot);
      
      // Limit history to 50 items to prevent memory issues
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      
      return newHistory;
    });
    
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  };

  const undo = () => {
    if (historyIndex <= 0) return;

    const previousState = history[historyIndex - 1];
    if (!previousState) return;

    setIsUndoRedoAction(true);
    
    // Restore page state
    setPages(prev => prev.map(page => 
      page.id === previousState.pageId 
        ? { ...page, ...previousState.pageState }
        : page
    ));

    // Restore AI-related state
    setAiGeneratedBlocks(previousState.aiGeneratedBlocks);
    setAiPrompts(previousState.aiPrompts);
    setAiOriginalContent(previousState.aiOriginalContent);
    
    // Clear any loading/streaming states to show content immediately
    setAiLoadingBlocks(new Set());
    setAiStreamingBlocks(new Set());

    setHistoryIndex(prev => prev - 1);
    
    setTimeout(() => setIsUndoRedoAction(false), 100);
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;

    const nextState = history[historyIndex + 1];
    if (!nextState) return;

    setIsUndoRedoAction(true);
    
    // Restore page state
    setPages(prev => prev.map(page => 
      page.id === nextState.pageId 
        ? { ...page, ...nextState.pageState }
        : page
    ));

    // Restore AI-related state
    setAiGeneratedBlocks(nextState.aiGeneratedBlocks);
    setAiPrompts(nextState.aiPrompts);
    setAiOriginalContent(nextState.aiOriginalContent);
    
    // Clear any loading/streaming states to show content immediately
    setAiLoadingBlocks(new Set());
    setAiStreamingBlocks(new Set());

    setHistoryIndex(prev => prev + 1);
    
    setTimeout(() => setIsUndoRedoAction(false), 100);
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Helper function to convert plain text to HTML preserving formatting



  // Helper to update pages for the current document
  const setPages = (newPagesOrUpdater) => {
    setDocuments(prevDocs => prevDocs.map(doc =>
      doc.id === currentDocumentId
        ? { ...doc, pages: typeof newPagesOrUpdater === 'function' ? newPagesOrUpdater(doc.pages) : newPagesOrUpdater }
        : doc
    ));
  };




  // Undo/Redo keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Check for Cmd+Shift+Z (Mac) or Ctrl+Y (Windows/Linux) for redo
      else if (((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') || ((e.ctrlKey) && e.key === 'y')) {
        e.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canUndo, canRedo]);

  // Save initial state to history when page loads or changes
  useEffect(() => {
    if (currentPageId && pages.length > 0) {
      const currentPage = pages.find(p => p.id === currentPageId);
      if (currentPage && history.length === 0) {
        // Save initial state
        saveToHistory('initial', 'Initial state');
      }
    }
  }, [currentPageId, pages.length, history.length, pages]);

  const renderPageTemplateDropdown = () => {
    if (!showPageTemplateDropdown) return null;

    return (
      <div className={styles.pageTemplateDropdown}>
        <div className={styles.dropdownHeader}>
          Choose a page template
        </div>
        {pageTemplates.map((template) => {
          const Icon = iconMap[template.icon];
          return (
            <button
              key={template.id}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => {
                addPage(template.id);
                setShowPageTemplateDropdown(false);
              }}
              className={styles.dropdownItem}
            >
              <Icon className={styles.dropdownIcon} />
              <div>
                <div className={styles.dropdownItemTitle}>{template.name}</div>
                <div className={styles.dropdownItemDescription}>{template.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };



  const renderTemplateDropdown = () => {
    if (!showTemplateDropdown) return null;

    // Filter all templates based on search
    const filterTemplate = (template, searchTerm) => {
      const term = searchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(term) ||
        (template.description && template.description.toLowerCase().includes(term))
      );
    };

    // Filter all templates (no distinction between AI and static)
    const filteredTemplates = templates.filter(template => 
      filterTemplate(template, templateSearch)
    );

    // Filter user templates
    const filteredUserTemplates = userTemplates.filter(template => 
      filterTemplate(template, templateSearch)
    );

    return (
      <div className={styles.templateModalOverlay} onClick={() => {
        setShowTemplateDropdown(false);
        setTemplateSearch('');
      }}>
        <div className={styles.templateModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.templateModalHeader}>
            <h2 className={styles.templateModalTitle}>
              <FileText className={styles.templateModalIcon} />
              Choose a Template
            </h2>
            <button
              className={styles.templateModalClose}
              onClick={() => {
                setShowTemplateDropdown(false);
                setTemplateSearch('');
              }}
            >
              <X className={styles.templateModalCloseIcon} />
            </button>
          </div>
        
          {/* Search section */}
          <div className={styles.templateModalSearch}>
            <div className={styles.templateSearchWrapper}>
              <Search className={styles.templateSearchIcon} />
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                className={styles.templateSearchInput}
                autoFocus
              />
              {templateSearch && (
                <button
                  onClick={() => setTemplateSearch('')}
                  className={styles.templateSearchClear}
                >
                  <X className={styles.templateSearchClearIcon} />
                </button>
              )}
            </div>
          </div>
          
          {/* Content area */}
          <div className={styles.templateModalContent}>
          {/* Show message if no results */}
          {filteredTemplates.length === 0 && 
           filteredUserTemplates.length === 0 && (
            <div className={styles.noTemplatesMessage}>
              No templates found matching "{templateSearch}"
            </div>
          )}
          
            {/* Templates */}
            {filteredTemplates.length > 0 && (
              <div className={styles.templateSection}>
                <h3 className={styles.templateSectionTitle}>
                  <FileText className={styles.templateSectionIcon} />
                  Templates
                </h3>
                <div className={styles.templateGrid}>
                  {filteredTemplates.map((template) => {
                    const Icon = iconMap[template.icon];
                    return (
                      <button
                        key={template.id}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          loadTemplate(template.id);
                          setShowTemplateDropdown(false);
                          setTemplateSearch('');
                        }}
                        className={styles.templateCard}
                      >
                        <div className={styles.templateCardHeader}>
                          <Icon className={styles.templateCardIcon} />
                        </div>
                        <h4 className={styles.templateCardTitle}>{template.name}</h4>
                        <p className={styles.templateCardDescription}>{template.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* User-created templates */}
            {filteredUserTemplates.length > 0 && (
              <div className={styles.templateSection}>
                <h3 className={styles.templateSectionTitle}>
                  <FileText className={styles.templateSectionIcon} />
                  User Templates
                </h3>
                <div className={styles.templateGrid}>
                  {filteredUserTemplates.map((template) => {
                    return (
                      <button
                        key={template.id}
                        onClick={() => {
                          setShowTemplateDropdown(false);
                          setTemplateSearch('');
                          // Parse the template data and create a new document
                          const templateData = JSON.parse(template.template_data);
                          const newDoc = {
                            ...templateData,
                            id: Date.now().toString(),
                            name: `${template.name} - Copy`
                          };
                          setDocuments([...documents, newDoc]);
                          setCurrentDocumentId(newDoc.id);
                          // Set the current page to the first page of the new document
                          if (newDoc.pages && newDoc.pages.length > 0) {
                            setCurrentPageId(newDoc.pages[0].id);
                          }
                        }}
                        className={styles.templateCard}
                      >
                        <div className={styles.templateCardHeader}>
                          <FileText className={styles.templateCardIcon} />
                          <span className={styles.userBadge}>Custom</span>
                        </div>
                        <h4 className={styles.templateCardTitle}>{template.name}</h4>
                        <p className={styles.templateCardDescription}>
                          {template.description || 'User-created template'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSlashMenu = () => {
    if (!showSlashMenu) return null;

    // Filter items based on search
    const filteredItems = slashMenuItems.filter(item => 
      item.label.toLowerCase().includes(slashMenuSearch.toLowerCase()) ||
      item.description.toLowerCase().includes(slashMenuSearch.toLowerCase()) ||
      (item.category || '').toLowerCase().includes(slashMenuSearch.toLowerCase())
    );

    // Group filtered items by category
    const categorizedItems = filteredItems.reduce((acc, item) => {
      const category = item.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    const categoryOrder = ['content', 'data', 'layout', 'interactive', 'media', 'utility'];
    const categoryLabels = {
      content: 'Content',
      data: 'Data & Visualization', 
      layout: 'Layout',
      interactive: 'Interactive',
      media: 'Media',
      utility: 'Utility'
    };

    return (
      <div className={styles.slashMenu}>
        <div className={styles.dropdownHeader}>
          Add a block
        </div>
        
        {/* Search Input */}
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search blocks..."
            value={slashMenuSearch}
            onChange={(e) => setSlashMenuSearch(e.target.value)}
            className={styles.searchInput}
            autoFocus
          />
        </div>

        {/* Scrollable Content */}
        <div className={styles.dropdownScrollableContent}>
          {Object.keys(categorizedItems).length === 0 ? (
            <div className={styles.noResults}>
              No blocks found matching "{slashMenuSearch}"
            </div>
          ) : (
            categoryOrder.map(category => {
              if (!categorizedItems[category]) return null;
              return (
                <div key={category}>
                  <div className={styles.dropdownCategoryHeader}>
                    {categoryLabels[category]}
                  </div>
                  {categorizedItems[category].map((item) => {
                    const Icon = iconMap[item.icon];
                    return (
                  <button
                    key={item.type}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={() => handleSlashMenuSelect(item.type)}
                    className={styles.dropdownItem}
                  >
                    <Icon className={styles.dropdownIcon} />
                    <div>
                      <div className={styles.dropdownItemTitle}>{item.label}</div>
                      <div className={styles.dropdownItemDescription}>{item.description}</div>
                    </div>
                  </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Render variable popup
  const renderVariablePopup = () => {
    if (!showVariablePopup) return null;

    // Group variables by category
    const categorizedVariables = availableVariables.reduce((acc, variable) => {
      const category = variable.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(variable);
      return acc;
    }, {});

    const categoryOrder = ['Financials', 'Company Info', 'Date/Time'];

    return (
      <div 
        className={`${styles.variablePopup} variable-popup`}
        style={{
          position: 'fixed',
          left: variablePopupPosition.x,
          top: variablePopupPosition.y
        }}
      >
        <div className={styles.variablePopupHeader}>
          Insert Variable
        </div>
        
        {categoryOrder.map(category => {
          const variables = categorizedVariables[category] || [];
          if (variables.length === 0) return null;

          return (
            <div key={category} className={styles.variableCategory}>
              <div className={styles.variableCategoryHeader}>
                {category}
              </div>
              {variables.map((variable, index) => {
                const globalIndex = availableVariables.indexOf(variable);
                return (
                  <div
                    key={variable.name}
                    className={`${styles.variableItem} ${globalIndex === selectedVariableIndex ? styles.selected : ''}`}
                    onClick={() => insertVariable(showVariablePopup.blockId, variable.name)}
                  >
                    <div className={styles.variableName}>
                      <span className={styles.variableBadge}>{'{{'}}</span>
                      {variable.label}
                    </div>
                    <div className={styles.variableDescription}>
                      {variable.description}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // Context-aware suggestion system
  // const getContextualSuggestions = () => {
  //   const currentPage = pages.find(p => p.id === currentPageId);
  //   if (!currentPage) return aiSuggestions;

  //   // Analyze current page content
  //   const hasFinancialData = currentPage.blocks?.some(block => 
  //     block.type === 'kpi' || block.type === 'chart' || block.type === 'table' || block.type === 'report'
  //   );
    
  //   const hasText = currentPage.blocks?.some(block => 
  //     block.type === 'text' && block.content?.text?.length > 50
  //   );
    
  //   const blockCount = currentPage.blocks?.length || 0;
    
  //   // Filter and prioritize suggestions based on context
  //   let contextualSuggestions = [...aiSuggestions];
    
  //   if (hasFinancialData) {
  //     // Prioritize financial analysis suggestions
  //     contextualSuggestions = aiSuggestions.filter(s => s.category === 'financial' || s.category === 'analysis');
  //   } else if (hasText && blockCount > 3) {
  //     // Prioritize content enhancement suggestions
  //     contextualSuggestions = aiSuggestions.filter(s => s.category === 'content' || s.category === 'enhancement');
  //   } else if (blockCount === 0) {
  //     // Prioritize document creation suggestions
  //     contextualSuggestions = aiSuggestions.filter(s => s.category === 'creation' || s.category === 'templates');
  //   }
    
  //   // Always include general suggestions if we have less than 6 contextual ones
  //   if (contextualSuggestions.length < 6) {
  //     const generalSuggestions = aiSuggestions.filter(s => 
  //       !contextualSuggestions.includes(s) && (s.category === 'general' || !s.category)
  //     );
  //     contextualSuggestions = [...contextualSuggestions, ...generalSuggestions].slice(0, 8);
  //   }
    
  //   return contextualSuggestions;
  // };


  // Render heading settings dialog
  const renderHeadingSettings = () => {
    if (!showHeadingSettings) return null;

    const currentPage = pages.find(p => p.id === currentPageId);
    const block = currentPage?.blocks?.find(b => b.id === showHeadingSettings);
    if (!block) return null;

    return (
      <div className={styles.slashMenu}>
        <div className={styles.dropdownHeader}>
          Heading Settings
        </div>
        
        <div className={styles.modalContent}>
          <label className={styles.modalLabel}>
            Heading Level:
          </label>
          <select
            value={block.content.level || 2}
            onChange={(e) => {
              updateBlockContent(showHeadingSettings, { level: parseInt(e.target.value) });
            }}
            className={styles.modalSelect}
          >
            <option value={1}>Heading 1 (H1)</option>
            <option value={2}>Heading 2 (H2)</option>
            <option value={3}>Heading 3 (H3)</option>
            <option value={4}>Heading 4 (H4)</option>
            <option value={5}>Heading 5 (H5)</option>
            <option value={6}>Heading 6 (H6)</option>
          </select>
          
          <div className={styles.modalActions}>
            <button
              onClick={() => setShowHeadingSettings(null)}
              className={styles.modalButton}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render video settings dialog
  const renderVideoSettings = () => {
    if (!showVideoSettings) return null;

    const currentPage = pages.find(p => p.id === currentPageId);
    const block = currentPage?.blocks?.find(b => b.id === showVideoSettings);
    if (!block) return null;

    const convertToEmbedUrl = (url) => {
      if (!url) return '';
      
      // YouTube URL conversion
      const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
      if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
      }
      
      // Loom URL conversion
      const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
      if (loomMatch) {
        return `https://www.loom.com/embed/${loomMatch[1]}`;
      }
      
      // Vimeo URL conversion
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      }
      
      return url; // Return as-is if no conversion needed
    };

    return (
      <div className={styles.slashMenu}>
        <div className={styles.dropdownHeader}>
          Video Settings
        </div>
        
        <div className={styles.modalContent}>
          <label className={styles.modalLabel}>
            Video URL:
          </label>
          <input
            type="url"
            value={block.content.url || ''}
            onChange={(e) => {
              const url = e.target.value;
              const embedUrl = convertToEmbedUrl(url);
              updateBlockContent(showVideoSettings, { url, embedUrl });
            }}
            placeholder="Enter YouTube, Loom, or Vimeo URL..."
            className={styles.modalInput}
          />
          <div className={styles.modalHelpText}>
            Supports YouTube, Loom, and Vimeo URLs
          </div>
          
          <div className={styles.modalActions}>
            <button
              onClick={() => setShowVideoSettings(null)}
              className={styles.modalButton}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to convert simple data format to Chart.js format
  const convertSimpleToChartJs = (simpleData, chartType) => {
    if (!simpleData || !Array.isArray(simpleData)) return null;
    
    return {
      labels: simpleData.map(item => item.label || item.name || item.x),
      datasets: [{
        label: 'Data',
        data: simpleData.map(item => item.value || item.y || item.val || 0),
        backgroundColor: chartType === 'pie' || chartType === 'donut' 
          ? simpleData.map((_, i) => `hsl(${i * 360 / simpleData.length}, 70%, 50%)`)
          : 'rgba(54, 162, 235, 0.6)',
        borderColor: chartType === 'pie' || chartType === 'donut' 
          ? simpleData.map((_, i) => `hsl(${i * 360 / simpleData.length}, 70%, 40%)`)
          : 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
  };

  // Render chart settings dialog
  const renderChartSettings = () => {
    if (!showChartSettings) return null;

    const currentPage = pages.find(p => p.id === currentPageId);
    const block = currentPage?.blocks?.find(b => b.id === showChartSettings);
    if (!block) return null;

    // Create chart item object compatible with ChartSettingsForm
    const chartItem = {
      id: block.id,
      chartType: block.content.chartType || 'bar',
      chartTitle: block.content.title || 'Chart Title',
      queryOptions: {
        dataGroups: block.content.dataGroups || [{ 
          dataType: 'GL Reports', 
          aggregation: 'SUM', 
          dataVal: '', 
          groupBy: '', 
          rowLimit: null, 
          groupByTime: undefined, 
          filters: [] 
        }],
        showLegend: block.content.showLegend || false,
        targetValue: block.content.targetValue || 0,
        filters: block.content.filters || []
      },
      color: block.content.color || '#007bff',
      // Use existing data (should already be in Chart.js format) or convert if in simple format
      data: block.content.data && block.content.data.datasets ? 
        block.content.data : 
        (block.content.data ? convertSimpleToChartJs(block.content.data, block.content.chartType) : null)
    };

    const handleChartSave = (updatedItem) => {
      // Update block content with chart settings
      // Keep the Chart.js format since the Chart component expects it
      updateBlockContent(showChartSettings, {
        chartType: updatedItem.chartType,
        title: updatedItem.chartTitle,
        dataGroups: updatedItem.queryOptions?.dataGroups,
        showLegend: updatedItem.queryOptions?.showLegend,
        targetValue: updatedItem.queryOptions?.targetValue,
        filters: updatedItem.queryOptions?.filters,
        color: updatedItem.color,
        data: updatedItem.data // Keep Chart.js format with datasets
      });
      setShowChartSettings(null);
    };

    const handleChartCancel = () => {
      setShowChartSettings(null);
    };

    return (
      <div 
        className={styles.chartSettingsOverlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowChartSettings(null);
          }
        }}
      >
        <div className={styles.chartSettingsPanel}>
          <ChartSettingsForm
            item={chartItem}
            onSave={handleChartSave}
            onCancel={handleChartCancel}
          />
        </div>
      </div>
    );
  };

  // Update placeholder behavior to remain invisible unless mouseover
  const renderBlockContent = (block, index) => {
    // Initialize layout from stored block data if not in state
    if (!blockLayouts[block.id] && block.layout?.width) {
      setBlockLayouts(prev => ({
        ...prev,
        [block.id]: {
          width: block.layout.width,
          ...prev[block.id]
        }
      }));
    }
    
    const currentLayout = blockLayouts[block.id] || (block.layout?.width ? { width: block.layout.width } : null);
    const isRightBlock = currentLayout?.isRightBlock;
    const hasRightBlock = currentLayout?.hasRightBlock;
    const showAddRight = currentLayout?.showAddRight;
    
    // Skip right blocks - they'll be rendered with their left block
    if (isRightBlock) return null;
    
    // Get the right block if this block has one
    const rightBlock = hasRightBlock ? blocks.find(b => b.id === hasRightBlock) : null;
    
    const blockElement = (
      <ReportBlock 
        key={block.id}
        block={block}
        mode="edit"
        onUpdate={updateBlockContent}
        onDelete={removeBlock}
        onDuplicate={(blockId) => {
          const currentPage = pages.find(p => p.id === currentPageId);
          if (!currentPage) return;
          const blockIndex = currentPage.blocks.findIndex(b => b.id === blockId);
          const block = currentPage.blocks[blockIndex];
          if (block && blockIndex !== -1) {
            duplicateBlock(block, blockIndex);
          }
        }}
        editingBlock={editingBlock}
        aiLoadingBlocks={aiLoadingBlocks}
        aiStreamingBlocks={aiStreamingBlocks}
        aiGeneratedBlocks={aiGeneratedBlocks}
        setEditingBlock={setEditingBlock}
        updateBlockContent={updateBlockContent}
        reportBlockExpandedAccounts={reportBlockExpandedAccounts}
        setReportBlockExpandedAccounts={setReportBlockExpandedAccounts}
        editingMarkdown={editingMarkdown}
        setEditingMarkdown={setEditingMarkdown}
        aiOriginalContent={aiOriginalContent}
        handleAIGeneration={handleAIPromptSubmit}
        handleAISettingsClick={handleAISettingsClick}
        handleEnhanceWithAI={handleEnhanceWithAI}
        onTextSelect={handleTextSelect}
        index={index}
        onDragStart={(e, block, index) => {
          setDraggedBlock({ block, index });
          e.dataTransfer.effectAllowed = 'move';
        }}
        draggable={true}
        textRefs={textRefs}
        renderTextWithVariables={renderTextWithVariables}
        handleTextInput={handleTextInput}
        showVariableValues={showVariableValues}
      />
    );

    // If there's no right block and no add button showing, render as single block
    if (!hasRightBlock && !showAddRight) {
      return (
        <div className={styles.blockContainer} style={{ width: currentLayout?.width ? `${currentLayout.width}%` : '100%' }}>
          {blockElement}
          <div 
            className={styles.resizeDivider}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleResizeStart(block.id, e.clientX);
              // Initialize block layout if it doesn't exist
              if (!blockLayouts[block.id]) {
                setBlockLayouts(prev => ({
                  ...prev,
                  [block.id]: { width: 100 }
                }));
              }
            }}
          >
            <div className={styles.resizeHandle}></div>
          </div>
        </div>
      );
    }

    // Render side-by-side layout
    return (
      <div className={styles.sideBySideContainer}>
        <div 
          className={styles.blockContainer}
          style={{ width: `${currentLayout?.width || 100}%`, position: 'relative' }}
        >
          {blockElement}
          <div 
            className={styles.resizeDividerSideBySide}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleResizeStart(block.id, e.clientX);
            }}
          >
            <div className={styles.resizeHandle}></div>
          </div>
        </div>

        {rightBlock && (
          <div 
            className={styles.blockContainer}
            style={{ width: `${100 - (currentLayout?.width || 100)}%` }}
          >
            <ReportBlock 
              key={rightBlock.id}
              block={rightBlock}
              mode="edit"
              onUpdate={updateBlockContent}
              onDelete={removeBlock}
              onDuplicate={(blockId) => {
                const currentPage = pages.find(p => p.id === currentPageId);
                if (!currentPage) return;
                const blockIndex = currentPage.blocks.findIndex(b => b.id === blockId);
                const block = currentPage.blocks[blockIndex];
                if (block && blockIndex !== -1) {
                  duplicateBlock(block, blockIndex);
                }
              }}
              editingBlock={editingBlock}
              aiLoadingBlocks={aiLoadingBlocks}
              aiStreamingBlocks={aiStreamingBlocks}
              aiGeneratedBlocks={aiGeneratedBlocks}
              setEditingBlock={setEditingBlock}
              updateBlockContent={updateBlockContent}
              reportBlockExpandedAccounts={reportBlockExpandedAccounts}
              setReportBlockExpandedAccounts={setReportBlockExpandedAccounts}
              editingMarkdown={editingMarkdown}
              setEditingMarkdown={setEditingMarkdown}
              aiOriginalContent={aiOriginalContent}
              handleAIGeneration={handleAIPromptSubmit}
              handleAISettingsClick={handleAISettingsClick}
              handleEnhanceWithAI={handleEnhanceWithAI}
              onTextSelect={handleTextSelect}
              index={index + 1}
              onDragStart={(e, block, index) => {
                setDraggedBlock({ block, index });
                e.dataTransfer.effectAllowed = 'move';
              }}
              draggable={true}
              textRefs={textRefs}
              renderTextWithVariables={renderTextWithVariables}
              handleTextInput={handleTextInput}
              showVariableValues={showVariableValues}
            />
          </div>
        )}

        {showAddRight && !rightBlock && (
          <div className={styles.addRightBlockContainer}>
            <button 
              className={styles.addRightBlockButton}
              onClick={() => addRightBlock(block.id, index)}
            >
              + Add Block
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render formatting menu
  const renderFormatMenu = () => {
    if (!showFormatMenu || !selection.rect) return null;
    
    // Position the menu directly above the selected text
    const menuHeight = 40; // px
    const menuWidth = 280; // approximate width
    
    // Calculate position directly above selection
    let top = selection.rect.top - menuHeight - 8; // 8px gap above selection
    let left = selection.rect.left + (selection.rect.width / 2) - (menuWidth / 2);
    
    // If menu would go off top of screen, show below selection
    if (top < 8) {
      top = selection.rect.bottom + 8;
    }
    
    // Clamp horizontal position to viewport
    const viewportWidth = window.innerWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > viewportWidth - 8) left = viewportWidth - menuWidth - 8;
    
    const style = {
      position: 'fixed',
      top,
      left,
      zIndex: 2000
    };
    
    return (
      <div ref={formatMenuRef} style={style} className={styles.fixedMenu}>
        <div className={styles.formatMenu}>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'bold')} 
            title="Bold (⌘B)" 
            className={styles.formatMenuButton}
          >
            <strong>B</strong>
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'italic')} 
            title="Italic (⌘I)" 
            className={styles.formatMenuButton}
          >
            <em>I</em>
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'underline')} 
            title="Underline (⌘U)" 
            className={styles.formatMenuButton}
          >
            <u>U</u>
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'strikethrough')} 
            title="Strikethrough" 
            className={styles.formatMenuButton}
          >
            <s>S</s>
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'code')} 
            title="Code" 
            className={styles.formatMenuButton}
          >
            {'</>'}
          </button>
          
          <div className={styles.formatMenuSeparator}></div>
          
          <button 
            onClick={() => applyFormatting(selection.blockId, 'color', '#e03e3e')} 
            title="Red text" 
            className={`${styles.formatMenuColorButton} ${styles.formatColorRed}`}
          >
            A
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'color', '#0f7b0f')} 
            title="Green text" 
            className={`${styles.formatMenuColorButton} ${styles.formatColorGreen}`}
          >
            A
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'color', '#0061cc')} 
            title="Blue text" 
            className={`${styles.formatMenuColorButton} ${styles.formatColorBlue}`}
          >
            A
          </button>
          
          <div className={styles.formatMenuSeparator}></div>
          
          <button 
            onClick={() => applyFormatting(selection.blockId, 'highlight', '#fff2dd')} 
            title="Yellow highlight" 
            className={styles.formatHighlightYellow}
          >
            A
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'highlight', '#ffebee')} 
            title="Red highlight" 
            className={styles.formatHighlightRed}
          >
            A
          </button>
          <button 
            onClick={() => applyFormatting(selection.blockId, 'highlight', '#e8f5e8')} 
            title="Green highlight" 
            className={styles.formatHighlightGreen}
          >
            A
          </button>
          
          <div className={styles.formatMenuSeparator}></div>
          
          <button 
            onClick={() => applyFormatting(selection.blockId, 'link')} 
            title="Create link" 
            className={styles.formatMenuButton}
          >
            🔗
          </button>
          
          <div className={styles.formatMenuSeparator}></div>
          
          <button 
            onClick={() => applyFormatting(selection.blockId, 'clear')} 
            title="Clear formatting" 
            className={styles.formatMenuButton}
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  // Render enhance text popup with options
  const renderEnhancePopup = () => {
    if (!showEnhancePopup) return null;

    const { blockId } = showEnhancePopup;
    
    // Get current text content (selected or entire block)
    const windowSelection = window.getSelection();
    let targetText = '';
    let isFullBlock = false;

    if (windowSelection && windowSelection.rangeCount > 0 && !windowSelection.isCollapsed) {
      targetText = windowSelection.toString().trim();
    } else {
      // No selection, use entire block
      const currentPage = pages.find(p => p.id === currentPageId);
      const block = currentPage?.blocks?.find(b => b.id === blockId);
      targetText = block?.content?.text || '';
      isFullBlock = true;
    }

    if (!targetText) return null;

    // Generate prompts from template using the target text
    const enhancementOptions = aiEnhancementOptions.map(option => ({
      ...option,
      prompt: option.promptTemplate.replace('{text}', targetText)
    }));

    const handleEnhanceOption = (option) => {
      setShowEnhancePopup(null);
      
      // Create selection data
      const selectionData = isFullBlock ? {
        blockId,
        selectedText: targetText,
        start: 0,
        end: targetText.length
      } : {
        blockId,
        selectedText: targetText,
        start: 0,
        end: targetText.length
      };
      
      handleAIEnhancement(blockId, option.prompt, selectionData);
    };

    return (
      <div className={`${styles.slashMenu} enhance-popup`}>
        <div className={styles.dropdownHeader}>
          Enhance Text {isFullBlock ? '(Entire Block)' : '(Selected Text)'}
        </div>
        
        <div className={styles.dropdownScrollableContent}>
          {enhancementOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => handleEnhanceOption(option)}
              className={styles.dropdownItem}
            >
              <div className={styles.dropdownIcon}>
                <Wand2 size={22} />
              </div>
              <div>
                <div className={styles.dropdownItemTitle}>{option.title}</div>
                <div className={styles.dropdownItemDescription}>{option.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Handler for "Enhance with AI" button in block actions - shows popup
  const handleEnhanceWithAI = (blockId) => {
    // Show the enhance popup for this block
    setShowEnhancePopup({ blockId });
  };

  // Block layout handlers
  const handleResizeStart = (blockId, startX) => {
    setResizingBlock({ blockId, startX, startWidth: blockLayouts[blockId]?.width || 100 });
  };

  const handleResizeMove = (e) => {
    if (!resizingBlock) return;
    
    e.preventDefault();
    
    const deltaX = e.clientX - resizingBlock.startX;
    // Try to get actual container width, fallback to 800
    const containerElement = document.querySelector(`[class*="reportBody"]`) || document.querySelector('.reportBody');
    const containerWidth = containerElement ? containerElement.offsetWidth : 800;
    const deltaPercent = (deltaX / containerWidth) * 100;
    let newWidth = Math.max(5, Math.min(95, resizingBlock.startWidth + deltaPercent));
    
    // Force immediate state update for real-time rendering
    setBlockLayouts(prev => {
      const currentLayout = prev[resizingBlock.blockId];
      const rightBlockId = currentLayout?.hasRightBlock;
      
      const updated = {
        ...prev,
        [resizingBlock.blockId]: {
          ...prev[resizingBlock.blockId],
          width: newWidth,
          canAddRightBlock: newWidth < 100
        }
      };
      
      // If there's a right block, update its width too
      if (rightBlockId) {
        updated[rightBlockId] = {
          ...prev[rightBlockId],
          width: 100 - newWidth
        };
      }
      
      return updated;
    });

    // Also update the block content to store the width persistently
    const currentPage = pages.find(p => p.id === currentPageId);
    if (currentPage) {
      const updatedBlocks = [...currentPage.blocks];
      const leftBlockIndex = updatedBlocks.findIndex(b => b.id === resizingBlock.blockId);
      
      if (leftBlockIndex !== -1) {
        // Update left block
        updatedBlocks[leftBlockIndex] = {
          ...updatedBlocks[leftBlockIndex],
          layout: { 
            ...updatedBlocks[leftBlockIndex].layout,
            width: newWidth 
          }
        };
        
        // Update right block if it exists
        const currentLayout = blockLayouts[resizingBlock.blockId];
        const rightBlockId = currentLayout?.hasRightBlock;
        if (rightBlockId) {
          const rightBlockIndex = updatedBlocks.findIndex(b => b.id === rightBlockId);
          if (rightBlockIndex !== -1) {
            updatedBlocks[rightBlockIndex] = {
              ...updatedBlocks[rightBlockIndex],
              layout: {
                ...updatedBlocks[rightBlockIndex].layout,
                width: 100 - newWidth
              }
            };
          }
        }
        
        updatePage(currentPageId, { blocks: updatedBlocks });
      }
    }
  };

  const handleResizeEnd = () => {
    if (resizingBlock) {
      const layout = blockLayouts[resizingBlock.blockId];
      if (layout && layout.width < 99 && !layout.hasRightBlock) {
        // Automatically create the right block when resizing
        const currentPage = pages.find(p => p.id === currentPageId);
        if (currentPage) {
          const blockIndex = currentPage.blocks.findIndex(b => b.id === resizingBlock.blockId);
          if (blockIndex !== -1) {
            addRightBlock(resizingBlock.blockId, blockIndex);
          }
        }
      }
    }
    setResizingBlock(null);
  };

  const addRightBlock = (leftBlockId, index) => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;

    const leftLayout = blockLayouts[leftBlockId];
    const leftWidth = leftLayout?.width || 100;
    const rightWidth = 100 - leftWidth;

    const newRightBlock = {
      id: Date.now().toString(),
      type: 'text',
      content: { text: '', isPlaceholder: true },
      isPlaceholder: true,
      layout: {
        width: rightWidth,
        isRightBlock: true,
        leftBlockId: leftBlockId
      }
    };

    // Update the layout state
    setBlockLayouts(prev => ({
      ...prev,
      [leftBlockId]: {
        ...prev[leftBlockId],
        hasRightBlock: newRightBlock.id,
        showAddRight: false
      },
      [newRightBlock.id]: {
        width: rightWidth,
        isRightBlock: true,
        leftBlockId: leftBlockId
      }
    }));

    // Update the left block to store its layout data
    const newBlocks = [...currentPage.blocks];
    const leftBlockIndex = newBlocks.findIndex(b => b.id === leftBlockId);
    if (leftBlockIndex !== -1) {
      newBlocks[leftBlockIndex] = {
        ...newBlocks[leftBlockIndex],
        layout: {
          ...newBlocks[leftBlockIndex].layout,
          width: leftWidth,
          hasRightBlock: newRightBlock.id
        }
      };
    }
    
    newBlocks.splice(index + 1, 0, newRightBlock);
    updatePage(currentPageId, { blocks: newBlocks });
    
    saveToHistory('add_side_block', 'Added side-by-side block');
  };

  // AI enhancement specifically for selected text or entire block
  const handleAIEnhancement = async (blockId, prompt, textSelection) => {
    if (!textSelection || !textSelection.selectedText) return;
    
    try {
      const element = textRefs.current[blockId];
      if (!element) return;
      
      const windowSelection = window.getSelection();
      const isFullBlockEnhancement = textSelection.start === 0 && textSelection.end === textSelection.selectedText.length;
      
      if (isFullBlockEnhancement) {
        // Enhance entire block - show loading in the element
        element.innerHTML = '<span style="font-style: italic; color: #6b7280;">✨ Enhancing...</span>';
        updateBlockContent(blockId, { text: element.innerHTML });
      } else if (windowSelection.rangeCount > 0) {
        // Enhance selected text only
        const range = windowSelection.getRangeAt(0);
        range.deleteContents();
        const loadingSpan = document.createElement('span');
        loadingSpan.textContent = '✨ Enhancing...';
        loadingSpan.style.fontStyle = 'italic';
        loadingSpan.style.color = '#6b7280';
        range.insertNode(loadingSpan);
        updateBlockContent(blockId, { text: element.innerHTML });
      }
      
      // Make AI API call - use the same endpoint as the existing AI functionality
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          messages: [{ role: 'user', content: prompt }],
          stream: false
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const enhancedText = data.choices?.[0]?.message?.content || textSelection.selectedText;
        
        if (isFullBlockEnhancement) {
          // Replace entire block content with sanitized AI output
          const sanitized = validateAIContent(enhancedText.replace(/\n/g, '<br>'));
          element.innerHTML = sanitized;
          updateBlockContent(blockId, { text: sanitized });
        } else {
          // Replace loading indicator with enhanced text
          const loadingSpan = element.querySelector('span[style*="italic"]');
          if (loadingSpan) {
            loadingSpan.textContent = enhancedText;
            loadingSpan.style.fontStyle = 'normal';
            loadingSpan.style.color = 'inherit';
            updateBlockContent(blockId, { text: element.innerHTML });
          }
        }
      } else {
        throw new Error('AI enhancement failed');
      }
    } catch (error) {
      console.error('AI enhancement error:', error);
      
      // Revert to original text on error
      const element = textRefs.current[blockId];
      if (element) {
        const isFullBlockEnhancement = textSelection.start === 0 && textSelection.end === textSelection.selectedText.length;
        
        if (isFullBlockEnhancement) {
          const sanitized = validateAIContent(textSelection.selectedText.replace(/\n/g, '<br>'));
          element.innerHTML = sanitized;
        } else {
          const loadingSpan = element.querySelector('span[style*="italic"]');
          if (loadingSpan) {
            loadingSpan.textContent = textSelection.selectedText;
            loadingSpan.style.fontStyle = 'normal';
            loadingSpan.style.color = 'inherit';
          }
        }
        updateBlockContent(blockId, { text: element.innerHTML });
      }
    }
  };

  // Helper to apply formatting to selected text
  const applyFormatting = (blockId, formatType, value) => {
    const element = textRefs.current[blockId];
    if (!element) return;
    
    // Convert textarea blocks to contentEditable with HTML formatting when first formatted
    if (element.tagName === 'TEXTAREA') {
      const currentPage = pages.find(p => p.id === currentPageId);
      if (!currentPage) return;
      
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (!block) return;
      
      // Convert plain text to HTML and update block content
      const plainText = block.content.text || '';
      const htmlText = plainText.replace(/\n/g, '<br>');
      updateBlockContent(blockId, { text: htmlText });
      
      // Re-render will create contentEditable element, then apply formatting
      setTimeout(() => {
        applyFormatting(blockId, formatType, value);
      }, 50);
      return;
    }
    
    // Apply formatting using modern DOM Selection API and manual element creation
    // Replaces deprecated execCommand with standard DOM manipulation
    const windowSelection = window.getSelection();
    if (windowSelection.rangeCount === 0 || windowSelection.isCollapsed) return;
    
    try {
      if (formatType === 'bold') {
        // Modern approach: Manually wrap selection in <strong> tag
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const strongElement = document.createElement('strong');
        strongElement.textContent = selectedText;
        range.insertNode(strongElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(strongElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'italic') {
        // Modern approach: Manually wrap selection in <em> tag
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const emElement = document.createElement('em');
        emElement.textContent = selectedText;
        range.insertNode(emElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(emElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'underline') {
        // Modern approach: Manually wrap selection in <u> tag
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const uElement = document.createElement('u');
        uElement.textContent = selectedText;
        range.insertNode(uElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(uElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'strikethrough') {
        // Modern approach: Manually wrap selection in <s> tag
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const sElement = document.createElement('s');
        sElement.textContent = selectedText;
        range.insertNode(sElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(sElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'color') {
        // Modern approach: Manually wrap selection in <span> with color style
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const spanElement = document.createElement('span');
        spanElement.style.color = value;
        spanElement.textContent = selectedText;
        range.insertNode(spanElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(spanElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'highlight') {
        // Modern approach: Manually wrap selection in <span> with background-color style
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const spanElement = document.createElement('span');
        spanElement.style.backgroundColor = value;
        spanElement.textContent = selectedText;
        range.insertNode(spanElement);

        // Position cursor after the inserted element
        const newRange = document.createRange();
        newRange.setStartAfter(spanElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'code') {
        // Modern approach: Manually wrap selection in <code> tag
        const range = windowSelection.getRangeAt(0);
        const selectedText = range.toString();
        range.deleteContents();
        const codeElement = document.createElement('code');
        codeElement.textContent = selectedText;
        range.insertNode(codeElement);
        
        // Position cursor after the inserted code
        const newRange = document.createRange();
        newRange.setStartAfter(codeElement);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      } else if (formatType === 'link') {
        const url = prompt('Enter URL:');
        if (url) {
          const range = windowSelection.getRangeAt(0);
          const selectedText = range.toString();
          range.deleteContents();
          const linkElement = document.createElement('a');
          linkElement.href = url;
          linkElement.target = '_blank';
          linkElement.textContent = selectedText;
          range.insertNode(linkElement);
          
          // Position cursor after the inserted link
          const newRange = document.createRange();
          newRange.setStartAfter(linkElement);
          newRange.collapse(true);
          windowSelection.removeAllRanges();
          windowSelection.addRange(newRange);
        }
      } else if (formatType === 'clear') {
        // Modern approach: Clear all formatting from selected text
        const range = windowSelection.getRangeAt(0);

        // Extract contents and get plain text (removes all HTML formatting)
        const fragment = range.extractContents();
        const plainText = fragment.textContent || '';

        // Insert plain text (no HTML tags, just text node)
        const textNode = document.createTextNode(plainText);
        range.insertNode(textNode);

        // Position cursor after the inserted text
        const newRange = document.createRange();
        newRange.setStartAfter(textNode);
        newRange.collapse(true);
        windowSelection.removeAllRanges();
        windowSelection.addRange(newRange);
      }
      
      // Update the block content with the new innerHTML
      updateBlockContent(blockId, { text: element.innerHTML });
      
    } catch (e) {
      console.error('Formatting command failed:', e);
    }
    
    setShowFormatMenu(false);
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    setTimeout(() => {
      setIsGeneratingPDF(false);
      alert('PDF generated successfully! Download started.');
    }, 2000);
  };



  // Show formatting menu on text selection
  const handleTextSelect = (blockId, e, mockSelection = null) => {
    if (mockSelection) {
      // Handle textarea selection
      setSelection({
        blockId: mockSelection.blockId,
        start: mockSelection.start,
        end: mockSelection.end,
        selectedText: mockSelection.selectedText,
        rect: {
          top: mockSelection.rect.top + window.scrollY,
          left: mockSelection.rect.left + window.scrollX,
          right: mockSelection.rect.right + window.scrollX,
          bottom: mockSelection.rect.bottom + window.scrollY,
          width: mockSelection.rect.width,
          height: mockSelection.rect.height
        }
      });
      setShowFormatMenu(true);
      return;
    }
    
    // Handle regular contentEditable selection
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      if (rect.width > 0 && rect.height > 0) {
        setSelection({ 
          blockId, 
          start: range.startOffset, 
          end: range.endOffset, 
          selectedText: selection.toString(),
          rect: {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            right: rect.right + window.scrollX,
            bottom: rect.bottom + window.scrollY,
            width: rect.width,
            height: rect.height
          }
        });
        setShowFormatMenu(true);
      }
    } else {
      setShowFormatMenu(false);
      setSelection({ blockId: null, start: null, end: null, rect: null });
    }
  };

  return (
    <div className={styles.container}>
      <CloseNav />
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div></div>
          <div className={styles.headerActions}>
            <button
              onClick={undo}
              disabled={!canUndo}
              className={styles.exportBtn}
              title="Undo (⌘Z)"
            >
              <Undo className={styles.exportIcon} />
              Undo
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              className={styles.exportBtn}
              title="Redo (⌘⇧Z)"
            >
              <Redo className={styles.exportIcon} />
              Redo
            </button>
            <button
              onClick={() => setIsCarouselMode(!isCarouselMode)}
              className={styles.toggleBtn}
              title={isCarouselMode ? 'Switch to Edit Mode' : 'Switch to Carousel View'}
            >
              {isCarouselMode ? (
                <>
                  <Edit className={styles.toggleIcon} />
                  Edit Mode
                </>
              ) : (
                <>
                  <Eye className={styles.toggleIcon} />
                  Carousel View
                </>
              )}
            </button>
            <button
              onClick={() => {
                if (showToolbarVariablePicker) {
                  setShowToolbarVariablePicker(false);
                  setSavedCursorInfo(null);
                } else {
                  const cursorInfo = checkActiveCursor();
                  if (cursorInfo) {
                    setSavedCursorInfo(cursorInfo);
                    setShowToolbarVariablePicker(true);
                    setToolbarVariableSearch('');
                    setSelectedToolbarVariableIndex(0);
                  }
                }
              }}
              disabled={!hasActiveCursor && !showToolbarVariablePicker}
              className={`${styles.exportBtn} ${(!hasActiveCursor && !showToolbarVariablePicker) ? styles.exportBtnDisabled : ''}`}
              title={hasActiveCursor ? "Insert Variable at Cursor" : "Click in a text block first to position cursor"}
            >
              <Plus className={styles.exportIcon} />
              Insert Variable
            </button>
            <button
              onClick={toggleVariableDisplay}
              className={styles.exportBtn}
              title={showVariableValues ? 'Show Variable Names' : 'Show Variable Values'}
            >
              <Variable className={styles.exportIcon} />
              {showVariableValues ? 'Show Names' : 'Show Values'}
            </button>
            <button
              onClick={() => setShowTemplateSaveDialog(true)}
              className={styles.exportBtn}
            >
              <Save className={styles.exportIcon} />
              Save as Template
            </button>
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              className={styles.exportBtn}
            >
              <Download className={styles.exportIcon} />
              {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>
      <div className={styles.main}>
        {isCarouselMode ? (
          // Carousel View - Using separate ReportCarousel component
          <ReportCarousel
            documents={documents}
            currentDocumentId={currentDocumentId}
            onToggleCarousel={() => setIsCarouselMode(false)}
            onDocumentChange={setCurrentDocumentId}
          />
        ) : (
          // Edit Mode
          <>
            <div className={styles.sidebar}>
              <div className={styles.sidebarInner}>
                <div className={styles.documentList}>
                  <h3 className={styles.documentListTitle}>Documents</h3>
                  <div className={styles.documentListItems}>
                    <div className={styles.documentDropdownContainer}>
                      {editingDocumentId === currentDocumentId ? (
                        <input
                          type="text"
                          className={styles.documentNameInput}
                          value={documents.find(d => d.id === currentDocumentId)?.name || ''}
                          onChange={(e) => {
                            setDocuments(prevDocs => prevDocs.map(d => 
                              d.id === currentDocumentId ? { ...d, name: e.target.value } : d
                            ));
                          }}
                          onBlur={() => setEditingDocumentId(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setEditingDocumentId(null);
                            }
                            if (e.key === 'Escape') {
                              setEditingDocumentId(null);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <select 
                          className={styles.documentDropdown}
                          value={currentDocumentId}
                          onChange={(e) => {
                            const selectedDocId = e.target.value;
                            setCurrentDocumentId(selectedDocId);
                            const selectedDoc = documents.find(d => d.id === selectedDocId);
                            if (selectedDoc) {
                              setCurrentPageId(selectedDoc.pages[0]?.id || '');
                            }
                          }}
                        >
                          {documents.map((doc) => (
                            <option key={doc.id} value={doc.id}>
                              {doc.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {editingDocumentId !== currentDocumentId && (
                        <>
                          <button
                            className={styles.documentEditButton}
                            onClick={() => setEditingDocumentId(currentDocumentId)}
                            title="Edit document name"
                          >
                            <Edit className={styles.documentEditIcon} />
                          </button>
                          <button
                            className={styles.documentDeleteButton}
                            onClick={() => {
                              const currentDoc = documents.find(d => d.id === currentDocumentId);
                              if (currentDoc && window.confirm(`Are you sure you want to delete "${currentDoc.name}"? This action cannot be undone.`)) {
                                setDocuments(prevDocs => prevDocs.filter(d => d.id !== currentDocumentId));
                                if (documents.length > 1) {
                                  const remainingDocs = documents.filter(d => d.id !== currentDocumentId);
                                  const nextDoc = remainingDocs[0];
                                  if (nextDoc) {
                                    setCurrentDocumentId(nextDoc.id);
                                    setCurrentPageId(nextDoc.pages[0]?.id || '');
                                  }
                                }
                              }
                            }}
                            title="Delete document"
                          >
                            <Trash2 className={styles.documentDeleteIcon} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    className={styles.addDocumentButton}
                    onClick={() => {
                      setShowTemplateDropdown((prev) => !prev);
                      if (!showTemplateDropdown) {
                        setTemplateSearch(''); // Reset search when opening
                      }
                    }}
                    aria-haspopup="true"
                    aria-expanded={showTemplateDropdown}
                  >
                    <Plus className={styles.addDocumentIcon} /> New Document
                  </button>
                  {showTemplateDropdown && renderTemplateDropdown()}
                </div>

                <div className={styles.pageList}>
                  <h3 className={styles.pageListTitle}>Pages</h3>
                  <div className={styles.pageListItems}>
                    {pages.map((page, index) => (
                      <div
                        key={page.id}
                        className={`${styles.pageListItem} ${currentPageId === page.id ? styles.pageListItemActive : ''}`}
                        onClick={() => setCurrentPageId(page.id)}
                        draggable
                        onDragStart={(e) => {
                          setDraggedPage({ page, index });
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverPageIndex(index);
                        }}
                        onDragLeave={(e) => {
                          // Only clear if we're leaving the entire page item
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setDragOverPageIndex(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (draggedPage && draggedPage.index !== index) {
                            movePage(draggedPage.index, index);
                          }
                          setDraggedPage(null);
                          setDragOverPageIndex(null);
                        }}
                        onDragEnd={() => {
                          setDraggedPage(null);
                          setDragOverPageIndex(null);
                        }}
                        className={draggedPage && draggedPage.page.id === page.id ? styles.dragOpacity : ''}
                      >
                        {/* Drag indicator */}
                        {dragOverPageIndex === index && draggedPage && draggedPage.page.id !== page.id && (
                          <div className={styles.pageDragIndicator}></div>
                        )}
                        
                        <div className={styles.pageListItemContent}>
                          <div className={styles.pageDragHandleWrapper}>
                            <GripVertical className={styles.pageDragHandle} />
                          </div>
                          <FileText className={styles.pageIcon} />
                          <input
                            type="text"
                            value={page.title}
                            onChange={(e) => updatePage(page.id, { title: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            className={styles.pageTitleInput}
                          />
                        
                          <span className={styles.pageIndex}>
                            {index + 1}
                          </span>
                          {pages.length > 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePage(page.id);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              className={styles.pageDeleteButton}
                              title="Delete page"
                            >
                              <Trash2 className={styles.pageDeleteIcon} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.pageTemplateWrapper}>
                  <button
                    onClick={() => setShowPageTemplateDropdown((prev) => !prev)}
                    className={styles.addPageButton}
                    aria-haspopup="true"
                    aria-expanded={showPageTemplateDropdown}
                  >
                    <Plus className={styles.addPageIcon} />
                    Add Page
                    <ChevronDown className={styles.chevronIcon} />
                  </button>
                  {showPageTemplateDropdown && renderPageTemplateDropdown()}
                </div>
              </div>
            </div>

            <div className={styles.contentWrapper}>
              <div className={styles.contentInner}>
                <div className={styles.reportPaper}>
                  <input
                  type="text"
                  value={currentPage.title || ''}
                  onChange={(e) => {
                    const updatedPages = pages.map(page =>
                      page.id === currentPageId
                        ? { ...page, title: e.target.value }
                        : page
                    );
                    setPages(updatedPages);
                  }}
                  className={styles.pageTitle}
                  placeholder="Page Title"
                />
                <div className={styles.reportBody}>
                    {blocks.map((block, index) => (
                      <div
                        key={block.id}
                        className={styles.blockGroup}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverIndex(index);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedBlock && draggedBlock.index !== index) {
                            moveBlock(draggedBlock.index, index);
                          }
                          setDraggedBlock(null);
                          setDragOverIndex(null);
                        }}
                        className={draggedBlock && draggedBlock.block.id === block.id ? styles.dragOpacity : ''}
                      >
                        {dragOverIndex === index && draggedBlock && (
                          <div className={styles.blockDragIndicator}></div>
                        )}
                        
                        {renderBlockContent(block, index)}
                      </div>
                    ))}
                    
                    {/* Show hover area for new block when no bottom empty block is visible */}
                    {!showBottomEmptyBlock && (
                      <div 
                        className={styles.bottomHoverArea}
                        onMouseEnter={() => setShowBottomEmptyBlock(true)}
                        onClick={() => setShowBottomEmptyBlock(true)}
                      />
                    )}
                    
                    {/* Show empty text block at bottom when hovering or clicked */}
                    {showBottomEmptyBlock && (
                      <div className={styles.emptyBlockWrapper}>
                        <div className={styles.blockContentWrapper}>
                          <div className={styles.emptyBlockDragHandle}>
                            <GripVertical className={styles.blockDragIcon} />
                          </div>

                          <div className={`${styles.blockInsertButtonWrapper} ${styles.emptyBlockInsertButton}`}>
                            <button className={styles.blockInsertButton}>
                              <Plus className={styles.blockInsertIcon} />
                            </button>
                          </div>

                          <div className={styles.emptyBlockContent}>
                            <div
                              ref={(el) => {
                                if (el) {
                                  textRefs.current['empty-block'] = el;
                                }
                              }}
                              contentEditable
                              onInput={(e) => {
                                const text = e.target.innerHTML;
                                if (text.trim() !== '') {
                                  // Create a new block with the content and clear this one
                                  const newBlockId = addBlock('text', blocks.length, { text });
                                  e.target.innerHTML = '';
                                  e.target.style.color = '#1e293b';
                                  setShowBottomEmptyBlock(false); // Hide after creating block
                                  setEditingBlock(newBlockId); // Ensure new block is in edit mode
                                  setTimeout(() => {
                                    const newTextRef = textRefs.current[newBlockId];
                                    if (newTextRef) {
                                      newTextRef.focus();
                                      
                                      // For different input types, set cursor appropriately
                                      if (newTextRef.tagName === 'INPUT' || newTextRef.tagName === 'TEXTAREA') {
                                        // For input/textarea, set cursor at end
                                        const textLength = newTextRef.value.length;
                                        newTextRef.setSelectionRange(textLength, textLength);
                                      } else {
                                        // For contentEditable, set cursor at end
                                        const selection = window.getSelection();
                                        const range = document.createRange();
                                        range.selectNodeContents(newTextRef);
                                        range.collapse(false);
                                        selection.removeAllRanges();
                                        selection.addRange(range);
                                      }
                                    }
                                  }, 100); // Increased timeout to ensure proper rendering
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === ' ' && e.target.innerText.trim() === '') {
                                  e.preventDefault();
                                  // Create an AI Content Generation block
                                  addBlock('ai', blocks.length);
                                  setShowBottomEmptyBlock(false);
                                  return;
                                }
                                if (e.key === '/' && e.target.innerText.trim() === '') {
                                  e.preventDefault();
                                  e.target.innerHTML = '/';
                                  e.target.style.color = '#1e293b';
                                  setShowSlashMenu({ blockId: 'empty-block', index: blocks.length });
                                }
                              }}
                              onFocus={(e) => {
                                e.target.innerHTML = '';
                                e.target.style.color = '#1e293b';
                              }}
                              onBlur={(e) => {
                                // Only clear and hide if no slash menu is showing
                                if (!showSlashMenu) {
                                  e.target.innerHTML = '';
                                  setTimeout(() => setShowBottomEmptyBlock(false), 100);
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (e.target.innerText.trim() === '') {
                                  e.target.dataset.placeholder = "Press '/' for commands or 'Space' for AI assistant";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.target.innerText.trim() === '') {
                                  e.target.dataset.placeholder = '';
                                }
                              }}
                              className={styles.emptyBlockTextArea}
                              suppressContentEditableWarning={true}
                            />
                          </div>
                          {/* No blockActionsWrapper (delete/clone) for placeholder block */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {showSlashMenu && (() => {
      // Only show slash menu if the block's text is exactly '/'
      let show = false;
      if (showSlashMenu.blockId === 'empty-block') {
        const el = textRefs.current['empty-block'];
        show = el && el.innerText.trim() === '/';
      } else {
        const block = blocks.find(b => b.id === showSlashMenu.blockId);
        show = block && block.content && typeof block.content.text === 'string' && block.content.text.trim() === '/';
      }
      return show ? (
        <div className={`${styles.fixedMenu} ${styles.fixedMenuCenter}`}>
          {renderSlashMenu()}
        </div>
      ) : null;
    })()}

    {/* Variable Popup */}
    {showVariablePopup && (
      <div>
        {renderVariablePopup()}
      </div>
    )}
                {showEnhancePopup && (
                  <div className={`${styles.fixedMenu} ${styles.fixedMenuCenter}`}>
                    {renderEnhancePopup()}
                  </div>
                )}
                {showHeadingSettings && (
                  <div className={`${styles.fixedMenu} ${styles.fixedMenuCenter}`}>
                    {renderHeadingSettings()}
                  </div>
                )}
                {showVideoSettings && (
                  <div className={`${styles.fixedMenu} ${styles.fixedMenuCenter}`}>
                    {renderVideoSettings()}
                  </div>
                )}
                {showChartSettings && renderChartSettings()}
                {renderFormatMenu()}
                
                {/* Toolbar Variable Picker */}
                {showToolbarVariablePicker && (
                  <div className={`${styles.fixedMenu} ${styles.fixedMenuCenter}`}>
                    <div className={`${styles.toolbarVariablePicker} toolbar-variable-picker`}>
                      <div className={styles.dropdownHeader}>
                        Insert Variable at Cursor
                      </div>
                      
                      {/* Search Input */}
                      <div className={styles.searchWrapper}>
                        <Search className={styles.searchIcon} />
                        <input
                          type="text"
                          placeholder="Search variables..."
                          value={toolbarVariableSearch}
                          onChange={(e) => setToolbarVariableSearch(e.target.value)}
                          className={styles.searchInput}
                          autoFocus
                        />
                      </div>
                      
                      {/* Variable List */}
                      <div className={styles.dropdownScrollableContent}>
                        {availableVariables
                          .filter(variable => 
                            variable.name.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
                            variable.label.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
                            variable.description.toLowerCase().includes(toolbarVariableSearch.toLowerCase())
                          )
                          .map((variable, index) => {
                            const value = getVariableValue(variable.name);
                            return (
                              <button
                                key={variable.name}
                                onClick={() => insertVariableAtCursor(variable.name)}
                                className={`${styles.toolbarVariableItem} ${
                                  index === selectedToolbarVariableIndex ? styles.toolbarVariableItemSelected : ''
                                }`}
                              >
                                <div className={styles.toolbarVariableInfo}>
                                  <div className={styles.toolbarVariableName}>
                                    <span className={styles.variableBadge}>{'{{'}}</span>
                                    {variable.label}
                                  </div>
                                  <div className={styles.toolbarVariableValue}>
                                    Current value: {value}
                                  </div>
                                  <div className={styles.variableDescription}>
                                    {variable.description}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        }
                        {availableVariables.filter(variable => 
                          variable.name.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
                          variable.label.toLowerCase().includes(toolbarVariableSearch.toLowerCase()) ||
                          variable.description.toLowerCase().includes(toolbarVariableSearch.toLowerCase())
                        ).length === 0 && (
                          <div className={styles.noResults}>
                            No variables found matching "{toolbarVariableSearch}"
                          </div>
                        )}
                      </div>
                      
                      {/* Close Button */}
                      <div className={styles.pickerActions}>
                        <button
                          onClick={() => {
                            setShowToolbarVariablePicker(false);
                            setToolbarVariableSearch('');
                          }}
                          className={styles.pickerCloseBtn}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Template Save Dialog */}
      <TemplateSaveDialog
        isOpen={showTemplateSaveDialog}
        onClose={() => setShowTemplateSaveDialog(false)}
        onSave={handleSaveAsTemplate}
        currentDocument={documents.find(d => d.id === currentDocumentId)}
      />
    </div>
  );
};
